import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ArrowLeft,
  ChevronRight,
  GitCompareArrows,
  AlertTriangle,
  Info,
  Flame,
  Layers,
  Route,
  Filter,
  Disc,
  Zap,
  ArrowUpRight,
  Target,
  Gauge,
  Menu,
  X,
} from 'lucide-react';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { rpc, RpcError } from '@/api/client';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { ToolHeader } from '@/components/common/ToolHeader';
import { SessionSelector } from '@/components/sector/SessionSelector';
import {
  LapSelector,
  type LapRef,
  type SessionWithLaps,
} from '@/components/lapdiff/LapSelector';
import { WizardStepper } from '@/components/sector/WizardStepper';
import { SessionRemovedAnalysisDialog } from '@/components/analysis/SessionRemovedAnalysisDialog';
import { useWizardState } from '@/hooks/useWizardState';
import { formatTime } from '@/lib/format';
import type {
  SessionSummary,
  ListSessionsResponse,
  ListAnalysisTracksResponse,
  SessionDetailResponse,
  AnalyzeAggregationDiffRequest,
  AnalyzeAggregationDiffResponse,
  AggregationSegment,
  AggregationSegmentDiff,
  AggregationLineDivergenceZone,
  AnalyzeResponse,
  GetAnalysisResponse,
  AnalysisRecord,
  ListAnalysesResponse,
  LapDiffMetricStats,
} from '@/types/telemetry';
import type { TrackShape, TrackAnnotation, TrackPoint, SplineSample } from '@/lib/spline';
import { sampleClosedSpline, findClosestParametricT, haversineDistanceM } from '@/lib/spline';
import { InteractiveTrackMap } from '@/components/common/trackmap/InteractiveTrackMap';
import type { OverlayUtils } from '@/components/common/trackmap/InteractiveTrackMap';
import { projectToLocalMeters, findClosestPointOnPolyline } from '@/lib/map/mapGeometry';
import type { GeoCoord, Point2D } from '@/lib/map/mapGeometry';
import {
  drawGpsLineDiffHeatmap,
  drawFasterSegmentsOverlay,
} from '@/lib/map/gpsHeatmap';
import { drawFinishLine } from '@/lib/map/trackGeometry';
import { ComparisonSummaryBanner } from '@/components/lapdiff/ComparisonSummaryBanner';
import { LapDiffTimeChart } from '@/components/lapdiff/LapDiffTimeChart';
import { DiffLegend } from '@/components/lapdiff/DiffLegend';
import {
  LAP_COLORS,
  getMetricUnit,
  formatLapDiffStats,
  formatLapDiffTimeDiff,
  computePointTimeSeries,
} from '@/lib/map/lapDiffMath';
import type {
  LapDiffMetric,
  LapDiffHoverInfo,
} from '@/lib/map/lapDiffMath';

function getSplineSampleAtDistanceM(
  targetDistM: number,
  samples: SplineSample[],
  totalLengthM: number
): { point: TrackPoint; normal: { x: number; y: number }; widthM: number } {
  const n = samples.length;
  if (n === 0) return { point: { lat: 0, lng: 0 }, normal: { x: 0, y: 1 }, widthM: 8.0 };
  if (totalLengthM <= 0) totalLengthM = 1.0;
  const d = ((targetDistM % totalLengthM) + totalLengthM) % totalLengthM;

  let low = 0,
    high = n - 1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (samples[mid].distanceM <= d) {
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  const i0 = Math.max(0, Math.min(n - 1, high));
  const i1 = (i0 + 1) % n;
  const s0 = samples[i0];
  const s1 = samples[i1];

  let d0 = s0.distanceM;
  let d1 = s1.distanceM;
  if (i1 === 0) {
    d1 = totalLengthM;
  }
  const span = d1 - d0;
  const u = span > 1e-6 ? Math.max(0, Math.min(1, (d - d0) / span)) : 0;

  const lat = s0.point.lat + u * (s1.point.lat - s0.point.lat);
  const lng = s0.point.lng + u * (s1.point.lng - s0.point.lng);
  const nx = s0.normal.x + u * (s1.normal.x - s0.normal.x);
  const ny = s0.normal.y + u * (s1.normal.y - s0.normal.y);
  const nLen = Math.sqrt(nx * nx + ny * ny) || 1;
  const widthM = s0.widthM + u * (s1.widthM - s0.widthM);

  return {
    point: { lat, lng },
    normal: { x: nx / nLen, y: ny / nLen },
    widthM,
  };
}

function getOffsetPoint(
  sample: { point: TrackPoint; normal: { x: number; y: number } },
  offsetM: number
): TrackPoint {
  const latRad = (sample.point.lat * Math.PI) / 180;
  const cosLat = Math.cos(latRad);
  const dLat = ((sample.normal.y * offsetM) / 6371000.0) * (180 / Math.PI);
  const dLon = ((sample.normal.x * offsetM) / (6371000.0 * cosLat)) * (180 / Math.PI);
  return {
    lat: sample.point.lat + dLat,
    lng: sample.point.lng + dLon,
  };
}

function getSegDistances(seg: AggregationSegment, totalLengthM: number) {
  const minDistM = seg.minDistanceM || (seg.minT || 0) * totalLengthM;
  const q1DistM = seg.q1DistanceM || (seg.q1T || 0) * totalLengthM;
  const medDistM = seg.medianDistanceM || (seg.medianT || 0) * totalLengthM;
  const q3DistM = seg.q3DistanceM || (seg.q3T || 0) * totalLengthM;
  const maxDistM = seg.maxDistanceM || (seg.maxT || 0) * totalLengthM;
  let iqrM = q3DistM - q1DistM;
  if (iqrM < 0) iqrM += totalLengthM;
  return { minDistM, q1DistM, medDistM, q3DistM, maxDistM, iqrM };
}

/**
 * Draws a band polygon along the track between lateral offsets `[leftFrac, rightFrac]`
 * from `distStartM` to `distEndM`. This enables split-track side-by-side comparison where Group A
 * occupies the left/inner half of the track width and Group B occupies the right/outer half.
 */
function drawLateralBandPolygon(
  distStartM: number,
  distEndM: number,
  finishDistM: number,
  totalLengthM: number,
  splineSamples: SplineSample[],
  project: (latRaw: number, lonRaw: number) => { x: number; y: number },
  ctx: CanvasRenderingContext2D,
  leftFrac: number,
  rightFrac: number,
  fillColor: string,
  strokeColor?: string,
  lineWidth?: number,
  dashed?: boolean
) {
  let spanM = distEndM - distStartM;
  if (spanM < 0) spanM += totalLengthM;
  if (spanM < 0.1) return;

  const numSteps = Math.max(4, Math.ceil(spanM / 2.0));
  const edgeA: Array<{ x: number; y: number }> = [];
  const edgeB: Array<{ x: number; y: number }> = [];

  for (let i = 0; i <= numSteps; i++) {
    const frac = i / numSteps;
    const d = (distStartM + frac * spanM) % totalLengthM;
    const absD = (finishDistM + d) % totalLengthM;
    const s = getSplineSampleAtDistanceM(absD, splineSamples, totalLengthM);
    const hw = (s.widthM || 8.0) / 2.0;
    const pA = getOffsetPoint(s, hw * leftFrac);
    const pB = getOffsetPoint(s, hw * rightFrac);
    edgeA.push(project(Math.round(pA.lat * 1e6), Math.round(pA.lng * 1e6)));
    edgeB.push(project(Math.round(pB.lat * 1e6), Math.round(pB.lng * 1e6)));
  }

  ctx.save();
  ctx.beginPath();
  edgeA.forEach((p, i) => {
    if (i === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  for (let i = edgeB.length - 1; i >= 0; i--) {
    ctx.lineTo(edgeB[i].x, edgeB[i].y);
  }
  ctx.closePath();
  ctx.fillStyle = fillColor;
  ctx.fill();
  if (strokeColor && lineWidth && lineWidth > 0) {
    if (dashed) ctx.setLineDash([4, 3]);
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = lineWidth;
    ctx.stroke();
  }
  ctx.restore();
}

function drawLateralTick(
  distFromFinishM: number,
  finishDistM: number,
  totalLengthM: number,
  splineSamples: SplineSample[],
  project: (latRaw: number, lonRaw: number) => { x: number; y: number },
  ctx: CanvasRenderingContext2D,
  leftFrac: number,
  rightFrac: number,
  color: string,
  lineWidth: number
): { midX: number; midY: number } {
  const absD = ((finishDistM + distFromFinishM) % totalLengthM + totalLengthM) % totalLengthM;
  const s = getSplineSampleAtDistanceM(absD, splineSamples, totalLengthM);
  const hw = (s.widthM || 8.0) / 2.0;
  const p1 = getOffsetPoint(s, hw * leftFrac);
  const p2 = getOffsetPoint(s, hw * rightFrac);
  const c1 = project(Math.round(p1.lat * 1e6), Math.round(p1.lng * 1e6));
  const c2 = project(Math.round(p2.lat * 1e6), Math.round(p2.lng * 1e6));

  ctx.beginPath();
  ctx.moveTo(c1.x, c1.y);
  ctx.lineTo(c2.x, c2.y);
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.stroke();

  return { midX: (c1.x + c2.x) * 0.5, midY: (c1.y + c2.y) * 0.5 };
}

type DiffReasonCategory = 'line' | 'braking' | 'acceleration' | 'upshift' | 'topspeed';
type DiffFilterCategory = 'all' | DiffReasonCategory;

interface UnifiedDiffItem {
  key: string;
  category: Exclude<DiffReasonCategory, 'topspeed'>;
  cornerName: string;
  distanceM: number;
  magnitudeM: number;
  spreadDiffM: number;
  estimatedTimeDiffMs?: number;
  segmentDiff?: AggregationSegmentDiff;
  lineZone?: AggregationLineDivergenceZone;
}

interface DiffRangeReason {
  category: DiffReasonCategory;
  distanceM: number;
  startDistanceM: number;
  endDistanceM: number;
  cornerName?: string;
  segmentDiff?: AggregationSegmentDiff;
  spreadDiffM?: number;
  lineZone?: AggregationLineDivergenceZone;
  topSpeedDiff?: {
    maxSpeedAKmh: number;
    maxSpeedBKmh: number;
    diffKmh: number;
    peakDistM: number;
  };
}

interface DiffRangeCluster {
  key: string;
  startDistanceM: number;
  endDistanceM: number;
  midDistanceM: number;
  cornerName: string;
  /** Integrated time difference (timeB - timeA) in ms over [startDistanceM, endDistanceM].
   *  Negative = Side B gained time; Positive = Side A gained time. */
  timeDiffMs: number;
  avgSpeedAKmh: number;
  avgSpeedBKmh: number;
  maxSpeedAKmh: number;
  maxSpeedBKmh: number;
  reasons: DiffRangeReason[];
}

function doesSpanOverlapRange(
  startM: number,
  endM: number,
  rangeMinM: number,
  rangeMaxM: number,
  totalLenM: number
): boolean {
  const s = ((startM % totalLenM) + totalLenM) % totalLenM;
  const e = ((endM % totalLenM) + totalLenM) % totalLenM;
  if (s <= e) {
    return Math.max(s, rangeMinM) <= Math.min(e, rangeMaxM);
  }
  return Math.max(s, rangeMinM) <= totalLenM || 0 <= Math.min(e, rangeMaxM);
}

function doesRangeClusterOverlapAnchors(
  cluster: DiffRangeCluster,
  anchors: [number, number] | null,
  totalLengthM: number
): boolean {
  if (!anchors) return true;
  const totalLen = Math.max(1.0, totalLengthM);
  const sMin = Math.min(anchors[0], anchors[1]);
  const sMax = Math.max(anchors[0], anchors[1]);
  return doesSpanOverlapRange(
    cluster.startDistanceM,
    cluster.endDistanceM,
    sMin * totalLen,
    sMax * totalLen,
    totalLen
  );
}

function doesDiffItemOverlapAnchors(
  item: UnifiedDiffItem,
  anchors: [number, number] | null,
  totalLengthM: number
): boolean {
  if (!anchors) return true;
  const totalLen = Math.max(1.0, totalLengthM);
  const sMin = Math.min(anchors[0], anchors[1]);
  const sMax = Math.max(anchors[0], anchors[1]);
  const rangeMinM = sMin * totalLen;
  const rangeMaxM = sMax * totalLen;

  if (item.lineZone) {
    return doesSpanOverlapRange(
      item.lineZone.startDistanceM,
      item.lineZone.endDistanceM,
      rangeMinM,
      rangeMaxM,
      totalLen
    );
  }
  if (item.segmentDiff) {
    const sd = item.segmentDiff;
    const dA = sd.segmentA ? getSegDistances(sd.segmentA, totalLen) : null;
    const dB = sd.segmentB ? getSegDistances(sd.segmentB, totalLen) : null;
    const overlapsA = dA
      ? doesSpanOverlapRange(dA.minDistM, dA.maxDistM, rangeMinM, rangeMaxM, totalLen)
      : false;
    const overlapsB = dB
      ? doesSpanOverlapRange(dB.minDistM, dB.maxDistM, rangeMinM, rangeMaxM, totalLen)
      : false;
    return overlapsA || overlapsB;
  }
  return item.distanceM >= rangeMinM && item.distanceM <= rangeMaxM;
}

export type DiffSideMode = 'lap' | 'sessions';

interface AggregationDiffAnalysisProps {
  trackName: string;
  initialSessionId?: string;
  initialMode?: DiffSideMode;
}

export function AggregationDiffAnalysis({
  trackName,
  initialSessionId,
  initialMode = 'sessions',
}: AggregationDiffAnalysisProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();
  const wizardSteps = [
    t('gearshifting.wizardChooseTrack'),
    `${t('aggregationdiff.groupA')} (${t('aggregationdiff.baseline')})`,
    `${t('aggregationdiff.groupB')} (${t('aggregationdiff.comparison')})`,
    t('gearshifting.wizardResults'),
  ];

  const initialSessionIds = useMemo(
    () =>
      initialSessionId
        ? initialSessionId
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
        : [],
    [initialSessionId]
  );

  const [wizardState, updateWizardState] = useWizardState({
    step: !trackName
      ? 0
      : initialSessionIds.length > 0 && initialMode === 'sessions'
      ? 2
      : 1,
    draftTrack: (trackName || undefined) as string | undefined,
    modeA: initialMode as DiffSideMode,
    modeB: initialMode as DiffSideMode,
    lapA: null as LapRef | null,
    lapB: null as LapRef | null,
    expandedSessions: (initialSessionIds.length > 0 ? [initialSessionIds[0]] : []) as string[],
    selectedIdsA: initialSessionIds as string[],
    selectedIdsB: [] as string[],
    labelA: '',
    labelB: '',
    driverFilterA: 'all',
    weatherFilterA: 'all',
    driverFilterB: 'all',
    weatherFilterB: 'all',
    showDiffHeatmap: true,
    showFasterSegments: true,
    showEventDiffs: true,
    dimSmallDiffs: false,
    diffFilter: 'all' as DiffFilterCategory,
    focusedDiffKey: '' as string,
  });

  const {
    step,
    draftTrack,
    modeA,
    modeB,
    lapA,
    lapB,
    expandedSessions: expandedSessionsArr,
    selectedIdsA: selectedIdsAArr,
    selectedIdsB: selectedIdsBArr,
    labelA,
    labelB,
    driverFilterA,
    weatherFilterA,
    driverFilterB,
    weatherFilterB,
    showDiffHeatmap,
    showFasterSegments,
    showEventDiffs,
    dimSmallDiffs,
    diffFilter,
    focusedDiffKey,
  } = wizardState;

  const [anchors, setAnchors] = useState<[number, number] | null>(null);
  const [layersMenuOpen, setLayersMenuOpen] = useState(false);
  const anchorCanvasCoordsRef = useRef<{ p0: Point2D; p1: Point2D } | null>(null);

  const selectedIdsA = useMemo(
    () => new Set(Array.isArray(selectedIdsAArr) ? selectedIdsAArr : []),
    [selectedIdsAArr]
  );
  const selectedIdsB = useMemo(
    () => new Set(Array.isArray(selectedIdsBArr) ? selectedIdsBArr : []),
    [selectedIdsBArr]
  );
  const expandedSessions = useMemo(
    () => new Set(Array.isArray(expandedSessionsArr) ? expandedSessionsArr : []),
    [expandedSessionsArr]
  );

  const setStep = useCallback(
    (newStep: number | ((prev: number) => number)) => {
      updateWizardState(prev => {
        const nextStep = typeof newStep === 'function' ? newStep(prev.step) : newStep;
        return { step: nextStep };
      }, false);
    },
    [updateWizardState]
  );

  const setDraftTrack = useCallback(
    (track: string | undefined) => updateWizardState({ draftTrack: track }),
    [updateWizardState]
  );

  const setSelectedIdsA = useCallback(
    (ids: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      updateWizardState(prev => {
        const prevArr = Array.isArray(prev.selectedIdsA) ? prev.selectedIdsA : [];
        const nextIds = typeof ids === 'function' ? ids(new Set(prevArr)) : ids;
        return { selectedIdsA: [...nextIds] };
      });
    },
    [updateWizardState]
  );

  const setSelectedIdsB = useCallback(
    (ids: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      updateWizardState(prev => {
        const prevArr = Array.isArray(prev.selectedIdsB) ? prev.selectedIdsB : [];
        const nextIds = typeof ids === 'function' ? ids(new Set(prevArr)) : ids;
        return { selectedIdsB: [...nextIds] };
      });
    },
    [updateWizardState]
  );

  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [sessionsWithLaps, setSessionsWithLaps] = useState<SessionWithLaps[]>([]);
  const loadingLapsRef = useRef<Set<string>>(new Set());
  const [availableTracks, setAvailableTracks] = useState<string[]>([]);
  const [savedAggregations, setSavedAggregations] = useState<AnalysisRecord[]>([]);
  const [trackId, setTrackId] = useState('');

  const [analysisResult, setAnalysisResult] = useState<AnalyzeAggregationDiffResponse | null>(null);
  const [segmentResult, setSegmentResult] = useState<AnalyzeAggregationDiffResponse | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [metric, setMetric] = useState<LapDiffMetric>('time');
  const [viewLap, setViewLap] = useState<'A' | 'B'>('A');
  const [hoveredProgress, setHoveredProgress] = useState<number | null>(null);
  const [hoverInfo, setHoverInfo] = useState<LapDiffHoverInfo | null>(null);
  const [selectedTrack, setSelectedTrack] = useState<string | undefined>(
    draftTrack || trackName || undefined
  );
  const [loading, setLoading] = useState(!trackName);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [sessionRemovedAnalysis, setSessionRemovedAnalysis] = useState<AnalysisRecord | null>(null);

  const [trackShape, setTrackShape] = useState<TrackShape | null>(null);
  const [trackAnnotations, setTrackAnnotations] = useState<TrackAnnotation[]>([]);
  const [hoveredItem, setHoveredItem] = useState<{
    item?: UnifiedDiffItem;
    x: number;
    y: number;
    pointA?: { lat: number; lng: number };
    pointB?: { lat: number; lng: number };
    speedAKmh?: number;
    speedBKmh?: number;
    speedDiffKmh?: number;
    lateralDiffM?: number;
    distanceM?: number;
    cornerName?: string;
  } | null>(null);

  const sameLapNumberDiffSession =
    modeA === 'lap' &&
    modeB === 'lap' &&
    lapA !== null &&
    lapB !== null &&
    lapA.lapNumber === lapB.lapNumber &&
    lapA.sessionId !== lapB.sessionId;

  const defaultLabelA =
    modeA === 'lap' && lapA
      ? sameLapNumberDiffSession
        ? `Lap ${lapA.lapNumber} (A)`
        : `Lap ${lapA.lapNumber}`
      : t('aggregationdiff.groupA');
  const defaultLabelB =
    modeB === 'lap' && lapB
      ? sameLapNumberDiffSession
        ? `Lap ${lapB.lapNumber} (B)`
        : `Lap ${lapB.lapNumber}`
      : t('aggregationdiff.groupB');

  const groupALabel = (labelA || defaultLabelA).trim();
  const groupBLabel = (labelB || defaultLabelB).trim();

  // Lazily fetch laps for a session when expanded in Single Lap mode
  const ensureSessionLapsLoaded = useCallback(async (sessionId: string) => {
    if (!sessionId || loadingLapsRef.current.has(sessionId)) return;
    loadingLapsRef.current.add(sessionId);
    setSessionsWithLaps(prev =>
      prev.map(s => (s.id === sessionId ? { ...s, loading: true } : s))
    );
    try {
      const detail = await rpc<SessionDetailResponse>('GetSession', { sessionId });
      const laps = detail.laps || [];
      setSessionsWithLaps(prev =>
        prev.map(s =>
          s.id === sessionId ? { ...s, laps, loading: false } : s
        )
      );
    } catch (err) {
      console.warn('Failed to load laps for session:', sessionId, err);
      loadingLapsRef.current.delete(sessionId);
      setSessionsWithLaps(prev =>
        prev.map(s => (s.id === sessionId ? { ...s, loading: false } : s))
      );
    }
  }, []);

  const toggleSessionExpand = useCallback(
    (sessionId: string) => {
      updateWizardState(prev => {
        const prevArr = Array.isArray(prev.expandedSessions) ? prev.expandedSessions : [];
        const next = new Set(prevArr);
        if (next.has(sessionId)) {
          next.delete(sessionId);
        } else {
          next.add(sessionId);
          ensureSessionLapsLoaded(sessionId);
        }
        return { expandedSessions: [...next] };
      });
    },
    [updateWizardState, ensureSessionLapsLoaded]
  );

  const handleSelectSideLap = useCallback(
    (side: 'A' | 'B', lap: LapRef | null) => {
      updateWizardState(prev => {
        if (side === 'A') {
          if (!lap) {
            return { lapA: null };
          }
          const isSame =
            prev.lapA?.sessionId === lap.sessionId &&
            prev.lapA?.lapNumber === lap.lapNumber;
          return {
            lapA: isSame ? null : lap,
          };
        } else {
          if (!lap) {
            return { lapB: null };
          }
          const isSame =
            prev.lapB?.sessionId === lap.sessionId &&
            prev.lapB?.lapNumber === lap.lapNumber;
          return {
            lapB: isSame ? null : lap,
          };
        }
      });
    },
    [updateWizardState]
  );

  // Load saved analysis record from Analysis History (supports both AGGREGATION_DIFF and legacy LAP_DIFF)
  const handleLoadAnalysis = useCallback(
    async (analysis: AnalysisRecord) => {
      const diffReq = analysis.aggregationDiff;
      const legacyLapReq = analysis.lapDiff;
      const trackInfo = diffReq?.track || legacyLapReq?.track;
      if (trackInfo) {
        const tName = trackInfo.trackName || trackInfo.trackId;
        setSelectedTrack(tName);
        setTrackId(trackInfo.trackId);
      }

      if (legacyLapReq) {
        updateWizardState({
          modeA: 'lap',
          modeB: 'lap',
          lapA: legacyLapReq.lapA
            ? {
                sessionId: legacyLapReq.lapA.sessionId,
                sessionDate: '',
                sessionTime: '',
                lapNumber: legacyLapReq.lapA.lapNumber,
                lapTimeMs: 0,
              }
            : null,
          lapB: legacyLapReq.lapB
            ? {
                sessionId: legacyLapReq.lapB.sessionId,
                sessionDate: '',
                sessionTime: '',
                lapNumber: legacyLapReq.lapB.lapNumber,
                lapTimeMs: 0,
              }
            : null,
        });
      } else if (diffReq) {
        updateWizardState({
          modeA: diffReq.lapA ? 'lap' : 'sessions',
          modeB: diffReq.lapB ? 'lap' : 'sessions',
          lapA: diffReq.lapA
            ? {
                sessionId: diffReq.lapA.sessionId,
                sessionDate: '',
                sessionTime: '',
                lapNumber: diffReq.lapA.lapNumber,
                lapTimeMs: 0,
              }
            : null,
          lapB: diffReq.lapB
            ? {
                sessionId: diffReq.lapB.sessionId,
                sessionDate: '',
                sessionTime: '',
                lapNumber: diffReq.lapB.lapNumber,
                lapTimeMs: 0,
              }
            : null,
          selectedIdsA: diffReq.sessionsA?.sessionIds || [],
          selectedIdsB: diffReq.sessionsB?.sessionIds || [],
          labelA: diffReq.labelA || '',
          labelB: diffReq.labelB || '',
        });
      }

      setAnalyzing(true);
      try {
        if (analysis.id) {
          const resp = await rpc<GetAnalysisResponse>('GetAnalysis', { id: analysis.id });
          const res = resp.aggregationDiff || (resp.lapDiff as unknown as AnalyzeAggregationDiffResponse);
          if (res) {
            setAnalysisResult(res);
            setStep(3);
          }
        } else if (diffReq) {
          const resp = await rpc<AnalyzeResponse>('Analyze', {
            aggregationDiff: diffReq,
            skipSave: true,
          });
          if (resp.aggregationDiff) {
            setAnalysisResult(resp.aggregationDiff);
            setStep(3);
          }
        } else if (legacyLapReq) {
          const resp = await rpc<AnalyzeResponse>('Analyze', {
            aggregationDiff: {
              track: legacyLapReq.track,
              lapA: legacyLapReq.lapA,
              lapB: legacyLapReq.lapB,
            },
            skipSave: true,
          });
          if (resp.aggregationDiff) {
            setAnalysisResult(resp.aggregationDiff);
            setStep(3);
          }
        }
      } catch (err: any) {
        console.error('Failed to evaluate loaded diff analysis:', err);
        const isSessionMissing =
          err?.status === 404 ||
          err?.isNotFound ||
          (err?.message && (err.message.includes('session') || err.message.includes('not found')));
        if (isSessionMissing) {
          setSessionRemovedAnalysis(analysis);
        } else {
          setError(err?.message || 'Failed to evaluate diff analysis');
        }
      } finally {
        setAnalyzing(false);
      }
    },
    [setStep, updateWizardState]
  );

  useEffect(() => {
    const stored = sessionStorage.getItem('loaded_analysis_record');
    if (stored) {
      sessionStorage.removeItem('loaded_analysis_record');
      try {
        const rec = JSON.parse(stored) as AnalysisRecord;
        if (
          rec.type === 'ANALYSIS_TYPE_AGGREGATION_DIFF' ||
          rec.type === 'ANALYSIS_TYPE_LAP_DIFF'
        ) {
          handleLoadAnalysis(rec);
        }
      } catch {}
    }
  }, [handleLoadAnalysis]);

  // Fetch available tracks
  useEffect(() => {
    let cancelled = false;
    rpc<ListAnalysisTracksResponse>('ListAnalysisTracks', {})
      .then(resp => {
        if (!cancelled) {
          setAvailableTracks(resp.tracks || []);
        }
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch saved single-group Aggregation analyses for quick-load dropdowns
  useEffect(() => {
    if (!selectedTrack) {
      setSavedAggregations([]);
      return;
    }
    let cancelled = false;
    rpc<ListAnalysesResponse>('ListAnalyses', {
      page: 1,
      pageSize: 50,
      type: 'ANALYSIS_TYPE_AGGREGATION',
      trackId: trackId || undefined,
    })
      .then(resp => {
        if (!cancelled) {
          setSavedAggregations(
            (resp.analyses || []).filter(
              a =>
                a.type === 'ANALYSIS_TYPE_AGGREGATION' &&
                (a.aggregation?.sessions?.sessionIds?.length ?? 0) > 0 &&
                (!selectedTrack ||
                  a.aggregation?.track?.trackName === selectedTrack ||
                  a.aggregation?.track?.trackId === trackId ||
                  a.aggregation?.track?.trackId === selectedTrack)
            )
          );
        }
      })
      .catch(() => {
        if (!cancelled) setSavedAggregations([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTrack, trackId]);

  // Fetch sessions and track shape for selected track
  useEffect(() => {
    if (!selectedTrack) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    loadingLapsRef.current.clear();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const listResp = await rpc<ListSessionsResponse>('ListSessions', {
          pageSize: 100,
          track_name: selectedTrack,
        });
        const filteredSessions = listResp.sessions || [];
        if (filteredSessions.length === 0) {
          setError(`No sessions found for track "${selectedTrack}"`);
          setLoading(false);
          return;
        }

        const detail = await rpc<SessionDetailResponse>('GetSession', {
          sessionId: filteredSessions[0].id,
        });
        const tId = detail.session.trackId;
        loadingLapsRef.current.add(filteredSessions[0].id);

        try {
          const shapeResp = await rpc<any>('GetTrackShapeAndAnnotations', { trackId: tId });
          if (shapeResp && !cancelled) {
            setTrackShape(shapeResp.shape || null);
            setTrackAnnotations(shapeResp.annotations || []);
          }
        } catch (err) {
          console.warn('Track shape not available for diff analysis:', err);
        }

        if (cancelled) return;

        setSessions(filteredSessions);
        setSessionsWithLaps(
          filteredSessions.map((s, idx) => ({
            ...s,
            laps: idx === 0 ? detail.laps || [] : [],
            loading: false,
          }))
        );
        setTrackId(tId);

        if (
          selectedIdsAArr.length === 0 &&
          selectedIdsBArr.length === 0 &&
          initialSessionIds.length === 0 &&
          !lapA &&
          !lapB
        ) {
          const sorted = [...filteredSessions].sort((a, b) => {
            const adt = (a.date || '') + ' ' + (a.time || '');
            const bdt = (b.date || '') + ' ' + (b.time || '');
            return bdt.localeCompare(adt);
          });
          const first = sorted[0];
          const second = sorted.length > 1 ? sorted[1] : sorted[0];
          updateWizardState(prev => ({
            selectedIdsA: [second.id],
            selectedIdsB: [first.id],
            expandedSessions:
              Array.isArray(prev.expandedSessions) && prev.expandedSessions.length > 0
                ? prev.expandedSessions
                : [filteredSessions[0].id],
          }));
        } else if (
          (!Array.isArray(expandedSessionsArr) || expandedSessionsArr.length === 0) &&
          filteredSessions.length > 0
        ) {
          updateWizardState({ expandedSessions: [filteredSessions[0].id] });
        }
      } catch (e: any) {
        if (!cancelled) {
          const nf = e instanceof RpcError && e.isNotFound;
          setNotFound(nf);
          setError(
            nf ? 'The requested resource could not be found.' : e.message || 'Failed to load data'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTrack]);

  // Ensure any pre-expanded sessions have their laps loaded
  useEffect(() => {
    if (sessionsWithLaps.length === 0 || !Array.isArray(expandedSessionsArr)) return;
    for (const sid of expandedSessionsArr) {
      const s = sessionsWithLaps.find(item => item.id === sid);
      if (s && s.laps.length === 0 && !loadingLapsRef.current.has(sid)) {
        ensureSessionLapsLoaded(sid);
      }
    }
  }, [expandedSessionsArr, sessionsWithLaps.length, ensureSessionLapsLoaded]);

  const isSideAReady = modeA === 'lap' ? lapA !== null : selectedIdsA.size > 0;
  const isSideBReady = modeB === 'lap' ? lapB !== null : selectedIdsB.size > 0;
  const isSameSingleLap =
    modeA === 'lap' &&
    modeB === 'lap' &&
    lapA !== null &&
    lapB !== null &&
    lapA.sessionId === lapB.sessionId &&
    lapA.lapNumber === lapB.lapNumber;
  const canCompare = Boolean(trackId && isSideAReady && isSideBReady && !isSameSingleLap);

  const handleAnalyze = useCallback(
    async (options?: { skipSave?: boolean }) => {
      if (!canCompare || !trackId) return;
      setAnalyzing(true);
      try {
        const resp = await rpc<AnalyzeResponse>('Analyze', {
          skipSave: options?.skipSave,
          aggregationDiff: {
            track: { trackId, trackName: selectedTrack },
            sessionsA: modeA === 'sessions' ? { sessionIds: [...selectedIdsA] } : undefined,
            sessionsB: modeB === 'sessions' ? { sessionIds: [...selectedIdsB] } : undefined,
            lapA:
              modeA === 'lap' && lapA
                ? { sessionId: lapA.sessionId, lapNumber: lapA.lapNumber }
                : undefined,
            lapB:
              modeB === 'lap' && lapB
                ? { sessionId: lapB.sessionId, lapNumber: lapB.lapNumber }
                : undefined,
            labelA: labelA.trim() || undefined,
            labelB: labelB.trim() || undefined,
          },
        });
        if (resp.aggregationDiff) {
          setAnalysisResult(resp.aggregationDiff);
          updateWizardState({ step: 3, focusedDiffKey: '' }, false);
        }
      } catch (e: any) {
        console.error(e);
      } finally {
        setAnalyzing(false);
      }
    },
    [
      canCompare,
      trackId,
      selectedTrack,
      modeA,
      modeB,
      selectedIdsA,
      selectedIdsB,
      lapA,
      lapB,
      labelA,
      labelB,
      updateWizardState,
    ]
  );

  // Deep-link re-evaluation on refresh
  useEffect(() => {
    if (step === 3 && !analysisResult && canCompare && !analyzing && trackId) {
      handleAnalyze({ skipSave: true });
    }
  }, [step, analysisResult, canCompare, analyzing, handleAnalyze, trackId]);

  const handleStepClick = useCallback(
    (targetStep: number) => {
      if (targetStep < step && selectedTrack) {
        setStep(targetStep);
      }
    },
    [step, selectedTrack, setStep]
  );

  const effectiveTotalLengthM =
    analysisResult?.totalLengthM || trackShape?.totalLengthM || 1000.0;

  const summaryA = analysisResult?.summaryA;
  const summaryB = analysisResult?.summaryB;
  const points = useMemo(() => analysisResult?.points || [], [analysisResult]);
  const fullLapAMs = summaryA?.medianLapMs || summaryA?.bestLapMs || 0;
  const fullLapBMs = summaryB?.medianLapMs || summaryB?.bestLapMs || 0;
  const activeResult = anchors && segmentResult ? segmentResult : analysisResult;

  const timeSeries = useMemo(
    () => computePointTimeSeries(points, fullLapAMs, fullLapBMs),
    [points, fullLapAMs, fullLapBMs]
  );

  const speedsA = useMemo(() => points.map(p => p.lapA?.speedKmh || 0), [points]);
  const speedsB = useMemo(() => points.map(p => p.lapB?.speedKmh || 0), [points]);

  // Calibrated per-station transit times derived directly from `timeSeries` (built from `points`)
  const stationTiming = useMemo(() => {
    const n = timeSeries.length;
    if (n < 3) return null;

    const totalLen = Math.max(1.0, effectiveTotalLengthM);

    // Integrates (timeB - timeA) in ms over [startDistM, endDistM] around the lap.
    // Negative = Side B took less time (Side B gained time); Positive = Side B took more time (Side B lost time).
    const computeIntervalTimeDiffMs = (startDistM: number, endDistM: number): number => {
      const spanM = Math.max(2.0, Math.min(totalLen, endDistM - startDistM));
      const numSteps = Math.max(4, Math.ceil((spanM / totalLen) * n * 2));
      const dtStepM = spanM / numSteps;
      let diffMs = 0;
      const stationSpanM = totalLen / n;
      for (let i = 0; i < numSteps; i++) {
        const midD = startDistM + (i + 0.5) * dtStepM;
        const wrappedD = ((midD % totalLen) + totalLen) % totalLen;
        const k = Math.min(n - 1, Math.max(0, Math.floor((wrappedD / totalLen) * n)));
        const weight = dtStepM / stationSpanM;
        diffMs += (timeSeries[k].segTimeBMs - timeSeries[k].segTimeAMs) * weight;
      }
      return diffMs;
    };

    const getSpeedAtDistanceKmh = (distM: number): number => {
      const wrappedD = ((distM % totalLen) + totalLen) % totalLen;
      const k = Math.min(n - 1, Math.max(0, Math.floor((wrappedD / totalLen) * n)));
      return Math.max(15.0, ((speedsA[k] || 0) + (speedsB[k] || 0)) * 0.5);
    };

    return {
      n,
      speedsA,
      speedsB,
      computeIntervalTimeDiffMs,
      getSpeedAtDistanceKmh,
    };
  }, [timeSeries, speedsA, speedsB, effectiveTotalLengthM]);

  // Build individual diff items for map canvas overlay rendering and hover hit-testing
  const allDiffItems = useMemo<UnifiedDiffItem[]>(() => {
    if (!analysisResult) return [];
    const items: UnifiedDiffItem[] = [];

    (analysisResult.segmentDiffs || []).forEach((sd, idx) => {
      const isBraking =
        sd.eventType === 'AGGREGATION_EVENT_TYPE_BRAKING' || sd.eventType === 1;
      const isAccel =
        sd.eventType === 'AGGREGATION_EVENT_TYPE_ACCELERATION' || sd.eventType === 2;
      const category: Exclude<DiffReasonCategory, 'topspeed'> = isBraking
        ? 'braking'
        : isAccel
        ? 'acceleration'
        : 'upshift';

      const isOnlyA = sd.matchStatus === 'only_a';
      const isOnlyB = sd.matchStatus === 'only_b';
      const dA = sd.segmentA ? getSegDistances(sd.segmentA, effectiveTotalLengthM) : null;
      const dB = sd.segmentB ? getSegDistances(sd.segmentB, effectiveTotalLengthM) : null;

      const spreadDiffM = dA && dB ? dB.iqrM - dA.iqrM : 0;
      const distM = sd.distanceM || dA?.medDistM || dB?.medDistM || 0;

      const mag =
        isOnlyA || isOnlyB
          ? 25.0 + Math.abs(sd.absDeltaM || 0)
          : Math.max(Math.abs(sd.deltaMedianM || 0), Math.abs(spreadDiffM) * 0.5);

      let zoneStartM = distM - 8;
      let zoneEndM = distM + 18;
      if (isOnlyA && dA) {
        zoneStartM = dA.q1DistM - 4;
        zoneEndM = dA.q3DistM + 20;
      } else if (isOnlyB && dB) {
        zoneStartM = dB.q1DistM - 4;
        zoneEndM = dB.q3DistM + 20;
      } else if (dA && dB) {
        zoneStartM = Math.min(dA.q1DistM, dB.q1DistM) - 4;
        zoneEndM = Math.max(dA.q3DistM, dB.q3DistM) + 22;
      }

      let estimatedTimeDiffMs: number | undefined;
      if (stationTiming) {
        estimatedTimeDiffMs = stationTiming.computeIntervalTimeDiffMs(zoneStartM, zoneEndM);
        const deltaMed = sd.deltaMedianM || 0;
        if (Math.abs(estimatedTimeDiffMs) < 5 && Math.abs(deltaMed) >= 0.5) {
          const localSpdMps = stationTiming.getSpeedAtDistanceKmh(distM) / 3.6;
          const sign = isBraking ? -1 : isAccel ? 1 : -0.5;
          estimatedTimeDiffMs = sign * (deltaMed / Math.max(4.0, localSpdMps)) * 220;
        }
      }

      items.push({
        key: `seg-${category}-${idx}-${Math.round(distM)}`,
        category,
        cornerName: sd.cornerName || '',
        distanceM: distM,
        magnitudeM: mag,
        spreadDiffM,
        estimatedTimeDiffMs,
        segmentDiff: sd,
      });
    });

    (analysisResult.lineDivergences || []).forEach((lz, idx) => {
      let estimatedTimeDiffMs: number | undefined;
      if (stationTiming) {
        estimatedTimeDiffMs = stationTiming.computeIntervalTimeDiffMs(
          lz.startDistanceM - 6,
          lz.endDistanceM + 10
        );
      } else if (
        lz.avgSpeedAKmh &&
        lz.avgSpeedAKmh > 2 &&
        lz.avgSpeedBKmh &&
        lz.avgSpeedBKmh > 2
      ) {
        const lenM = Math.max(2.0, lz.endDistanceM - lz.startDistanceM);
        const tAMs = (lenM / (lz.avgSpeedAKmh / 3.6)) * 1000;
        const tBMs = (lenM / (lz.avgSpeedBKmh / 3.6)) * 1000;
        estimatedTimeDiffMs = tBMs - tAMs;
      }

      if (
        estimatedTimeDiffMs === undefined ||
        !Number.isFinite(estimatedTimeDiffMs) ||
        Math.abs(estimatedTimeDiffMs) < 50
      ) {
        return;
      }

      items.push({
        key: `line-${idx}-${Math.round(lz.startDistanceM)}`,
        category: 'line',
        cornerName: lz.cornerName || '',
        distanceM: lz.peakDistanceM || lz.startDistanceM,
        magnitudeM: (lz.maxLateralDiffM || 0) * 3.0,
        spreadDiffM: 0,
        estimatedTimeDiffMs,
        lineZone: lz,
      });
    });

    items.sort((a, b) => {
      const timeDiff =
        Math.abs(b.estimatedTimeDiffMs || 0) -
        Math.abs(a.estimatedTimeDiffMs || 0);
      if (Math.abs(timeDiff) > 1e-6) {
        return timeDiff;
      }
      return b.magnitudeM - a.magnitudeM;
    });
    return items;
  }, [analysisResult, effectiveTotalLengthM, stationTiming]);

  // Cluster continuous segments of time lost/gained along the track, then attach the major
  // contributing reasons (Braking, Driving Line, Acceleration, Upshift, Top Speed) within each cluster.
  const allRangeClusters = useMemo<DiffRangeCluster[]>(() => {
    if (!analysisResult) return [];
    const totalLen = Math.max(1.0, effectiveTotalLengthM);
    const n = timeSeries.length;

    interface WorkingCluster {
      startK: number;
      endK: number;
      startDistanceM: number;
      endDistanceM: number;
      timeDiffMs: number;
      avgSpeedAKmh: number;
      avgSpeedBKmh: number;
      maxSpeedAKmh: number;
      maxSpeedBKmh: number;
      peakSpeedDistM: number;
      cornerNames: string[];
      reasons: DiffRangeReason[];
    }

    const rawClusters: WorkingCluster[] = [];

    if (n >= 3) {
      const stationSpanM = totalLen / n;
      // dtB[k] < 0 means Group B gained time (Group A lost time); > 0 means Group A gained time (Group B lost time)
      const dtB = timeSeries.map(ts => ts.segTimeBMs - ts.segTimeAMs);
      const smoothDt = dtB.map((_, k) => {
        const prev = dtB[(k - 1 + n) % n];
        const next = dtB[(k + 1) % n];
        return 0.25 * prev + 0.5 * dtB[k] + 0.25 * next;
      });
      const deadbandMs = Math.max(0.25, 10.0 / n);

      interface StationRun {
        startK: number;
        endK: number;
        sign: -1 | 1;
      }

      const initialRuns: StationRun[] = [];
      let k = 0;
      while (k < n) {
        const s: -1 | 0 | 1 =
          smoothDt[k] < -deadbandMs ? -1 : smoothDt[k] > deadbandMs ? 1 : 0;
        if (s === 0) {
          k++;
          continue;
        }
        let endK = k;
        while (endK + 1 < n) {
          const nextS: -1 | 0 | 1 =
            smoothDt[endK + 1] < -deadbandMs
              ? -1
              : smoothDt[endK + 1] > deadbandMs
              ? 1
              : 0;
          if (nextS !== s) break;
          endK++;
        }
        initialRuns.push({ startK: k, endK, sign: s });
        k = endK + 1;
      }

      // Bridge short neutral gaps (<= 25m) between consecutive runs of the same sign
      const mergedRuns: StationRun[] = [];
      for (const run of initialRuns) {
        if (mergedRuns.length > 0) {
          const prev = mergedRuns[mergedRuns.length - 1];
          const gapStations = run.startK - prev.endK - 1;
          const gapDistM = gapStations * stationSpanM;
          if (prev.sign === run.sign && gapDistM <= 25.0) {
            let gapOppositeMs = 0;
            for (let g = prev.endK + 1; g < run.startK; g++) {
              if (dtB[g] * prev.sign < 0) {
                gapOppositeMs += Math.abs(dtB[g]);
              }
            }
            if (gapOppositeMs < 15.0) {
              prev.endK = run.endK;
              continue;
            }
          }
        }
        mergedRuns.push({ ...run });
      }

      // Split ultra-long runs (> 150m) if there is a clear interior valley between two strong sub-clusters
      const finalRuns: StationRun[] = [];
      const maxSingleRunStations = Math.max(8, Math.ceil(150.0 / stationSpanM));
      for (const run of mergedRuns) {
        if (run.endK - run.startK + 1 > maxSingleRunStations) {
          let peakAbs = 0;
          for (let i = run.startK; i <= run.endK; i++) {
            peakAbs = Math.max(peakAbs, Math.abs(smoothDt[i]));
          }
          const margin = Math.max(3, Math.floor((run.endK - run.startK) * 0.25));
          let valleyK = -1;
          let valleyMinAbs = Infinity;
          for (let i = run.startK + margin; i <= run.endK - margin; i++) {
            const absVal = Math.abs(smoothDt[i]);
            if (absVal < valleyMinAbs) {
              valleyMinAbs = absVal;
              valleyK = i;
            }
          }
          if (valleyK > run.startK && valleyMinAbs < peakAbs * 0.25) {
            let sum1 = 0;
            let sum2 = 0;
            for (let i = run.startK; i <= valleyK; i++) sum1 += dtB[i];
            for (let i = valleyK + 1; i <= run.endK; i++) sum2 += dtB[i];
            if (Math.abs(sum1) >= 50 && Math.abs(sum2) >= 50) {
              finalRuns.push({ startK: run.startK, endK: valleyK, sign: run.sign });
              finalRuns.push({ startK: valleyK + 1, endK: run.endK, sign: run.sign });
              continue;
            }
          }
        }
        finalRuns.push(run);
      }

      for (const run of finalRuns) {
        let netTimeDiffMs = 0;
        let sumSpdA = 0;
        let sumSpdB = 0;
        let maxSpdA = 0;
        let maxSpdB = 0;
        let peakSpdK = run.startK;
        let maxPeakSpd = 0;
        const count = run.endK - run.startK + 1;

        for (let i = run.startK; i <= run.endK; i++) {
          netTimeDiffMs += dtB[i];
          const sA = speedsA[i] || 0;
          const sB = speedsB[i] || 0;
          sumSpdA += sA;
          sumSpdB += sB;
          if (sA > maxSpdA) maxSpdA = sA;
          if (sB > maxSpdB) maxSpdB = sB;
          const pairMax = Math.max(sA, sB);
          if (pairMax > maxPeakSpd) {
            maxPeakSpd = pairMax;
            peakSpdK = i;
          }
        }

        if (Math.abs(netTimeDiffMs) < 50) continue;

        rawClusters.push({
          startK: run.startK,
          endK: run.endK,
          startDistanceM: run.startK * stationSpanM,
          endDistanceM: (run.endK + 1) * stationSpanM,
          timeDiffMs: netTimeDiffMs,
          avgSpeedAKmh: count > 0 ? sumSpdA / count : 0,
          avgSpeedBKmh: count > 0 ? sumSpdB / count : 0,
          maxSpeedAKmh: maxSpdA,
          maxSpeedBKmh: maxSpdB,
          peakSpeedDistM: (peakSpdK + 0.5) * stationSpanM,
          cornerNames: [],
          reasons: [],
        });
      }
    }

    // Fallback when station time series is unavailable or has no >=50ms cluster
    if (rawClusters.length === 0 && allDiffItems.length > 0) {
      const sigItems = allDiffItems
        .filter(
          it =>
            it.estimatedTimeDiffMs !== undefined &&
            Number.isFinite(it.estimatedTimeDiffMs) &&
            Math.abs(it.estimatedTimeDiffMs) >= 50
        )
        .sort((a, b) => a.distanceM - b.distanceM);

      for (const it of sigItems) {
        const itStart = it.lineZone
          ? it.lineZone.startDistanceM
          : Math.max(0, it.distanceM - 12);
        const itEnd = it.lineZone
          ? it.lineZone.endDistanceM
          : Math.min(totalLen, it.distanceM + 16);
        const prev = rawClusters[rawClusters.length - 1];
        if (prev && itStart <= prev.endDistanceM + 35) {
          prev.endDistanceM = Math.max(prev.endDistanceM, itEnd);
          if (Math.abs(it.estimatedTimeDiffMs || 0) > Math.abs(prev.timeDiffMs)) {
            prev.timeDiffMs = it.estimatedTimeDiffMs || prev.timeDiffMs;
          }
          if (it.cornerName && !prev.cornerNames.includes(it.cornerName)) {
            prev.cornerNames.push(it.cornerName);
          }
        } else {
          rawClusters.push({
            startK: 0,
            endK: 0,
            startDistanceM: itStart,
            endDistanceM: itEnd,
            timeDiffMs: it.estimatedTimeDiffMs || 0,
            avgSpeedAKmh: it.lineZone?.avgSpeedAKmh || 0,
            avgSpeedBKmh: it.lineZone?.avgSpeedBKmh || 0,
            maxSpeedAKmh: it.lineZone?.avgSpeedAKmh || 0,
            maxSpeedBKmh: it.lineZone?.avgSpeedBKmh || 0,
            peakSpeedDistM: it.distanceM,
            cornerNames: it.cornerName ? [it.cornerName] : [],
            reasons: [],
          });
        }
      }
    }

    if (rawClusters.length === 0) return [];

    // 1. Attribute Braking, Acceleration, and Upshift segmentDiffs to their best matching cluster
    for (const sd of analysisResult.segmentDiffs || []) {
      const isBraking =
        sd.eventType === 'AGGREGATION_EVENT_TYPE_BRAKING' || sd.eventType === 1;
      const isAccel =
        sd.eventType === 'AGGREGATION_EVENT_TYPE_ACCELERATION' || sd.eventType === 2;
      const category: Exclude<DiffReasonCategory, 'line' | 'topspeed'> = isBraking
        ? 'braking'
        : isAccel
        ? 'acceleration'
        : 'upshift';

      const isOnlyA = sd.matchStatus === 'only_a';
      const isOnlyB = sd.matchStatus === 'only_b';
      const dA = sd.segmentA ? getSegDistances(sd.segmentA, totalLen) : null;
      const dB = sd.segmentB ? getSegDistances(sd.segmentB, totalLen) : null;
      const spreadDiffM = dA && dB ? dB.iqrM - dA.iqrM : 0;
      const distM = sd.distanceM || dA?.medDistM || dB?.medDistM || 0;

      if (!isOnlyA && !isOnlyB) {
        if (Math.abs(sd.deltaMedianM || 0) < 0.5 && Math.abs(spreadDiffM) < 1.0) {
          continue;
        }
        if (
          dimSmallDiffs &&
          Math.abs(sd.deltaMedianM || 0) < 1.5 &&
          Math.abs(spreadDiffM) < 2.0
        ) {
          continue;
        }
      }

      const padBefore = isBraking ? 25.0 : 12.0;
      const padAfter = isBraking ? 10.0 : 20.0;

      let bestCluster: WorkingCluster | null = null;
      let bestDistToCluster = Infinity;

      for (const c of rawClusters) {
        let distToRange = 0;
        if (distM < c.startDistanceM) {
          distToRange = c.startDistanceM - distM;
          if (distToRange > padBefore) continue;
        } else if (distM > c.endDistanceM) {
          distToRange = distM - c.endDistanceM;
          if (distToRange > padAfter) continue;
        }
        if (distToRange < bestDistToCluster) {
          bestDistToCluster = distToRange;
          bestCluster = c;
        }
      }

      if (!bestCluster) continue;

      if (sd.cornerName && !bestCluster.cornerNames.includes(sd.cornerName)) {
        bestCluster.cornerNames.push(sd.cornerName);
      }

      const segMinM =
        dA && dB
          ? Math.min(dA.minDistM, dB.minDistM)
          : dA?.minDistM ?? dB?.minDistM ?? distM;
      const segMaxM =
        dA && dB
          ? Math.max(dA.maxDistM, dB.maxDistM)
          : dA?.maxDistM ?? dB?.maxDistM ?? distM;

      const candidateReason: DiffRangeReason = {
        category,
        distanceM: distM,
        startDistanceM: segMinM,
        endDistanceM: segMaxM,
        cornerName: sd.cornerName || undefined,
        segmentDiff: sd,
        spreadDiffM,
      };

      const existingIdx = bestCluster.reasons.findIndex(r => r.category === category);
      if (existingIdx >= 0) {
        const existing = bestCluster.reasons[existingIdx];
        const existingSd = existing.segmentDiff;
        const existingMag =
          existingSd?.matchStatus === 'only_a' || existingSd?.matchStatus === 'only_b'
            ? 100
            : Math.max(
                Math.abs(existingSd?.deltaMedianM || 0),
                Math.abs(existing.spreadDiffM || 0) * 0.5
              );
        const newMag =
          isOnlyA || isOnlyB
            ? 100
            : Math.max(Math.abs(sd.deltaMedianM || 0), Math.abs(spreadDiffM) * 0.5);
        if (newMag > existingMag) {
          bestCluster.reasons[existingIdx] = candidateReason;
        }
      } else {
        bestCluster.reasons.push(candidateReason);
      }
    }

    // 2. Attribute Driving Line divergence zones to each cluster (consolidated to at most 1 line reason per range)
    const minLineLateralM = dimSmallDiffs ? 0.55 : 0.35;
    for (const c of rawClusters) {
      const matchingZones = (analysisResult.lineDivergences || []).filter(lz => {
        if ((lz.maxLateralDiffM || 0) < minLineLateralM) return false;
        return (
          Math.max(lz.startDistanceM, c.startDistanceM - 8) <=
          Math.min(lz.endDistanceM, c.endDistanceM + 8)
        );
      });

      if (matchingZones.length > 0) {
        let peakZone = matchingZones[0];
        let minStart = matchingZones[0].startDistanceM;
        let maxEnd = matchingZones[0].endDistanceM;
        let weightedMeanSum = 0;
        let weightSum = 0;

        for (const lz of matchingZones) {
          if ((lz.maxLateralDiffM || 0) > (peakZone.maxLateralDiffM || 0)) {
            peakZone = lz;
          }
          minStart = Math.min(minStart, lz.startDistanceM);
          maxEnd = Math.max(maxEnd, lz.endDistanceM);
          const w = Math.max(1.0, lz.endDistanceM - lz.startDistanceM);
          weightedMeanSum += (lz.meanLateralDiffM || 0) * w;
          weightSum += w;
          if (lz.cornerName && !c.cornerNames.includes(lz.cornerName)) {
            c.cornerNames.push(lz.cornerName);
          }
        }

        const mergedLz: AggregationLineDivergenceZone = {
          ...peakZone,
          startDistanceM: Math.max(c.startDistanceM, minStart),
          endDistanceM: Math.min(c.endDistanceM, maxEnd),
          meanLateralDiffM:
            weightSum > 0 ? weightedMeanSum / weightSum : peakZone.meanLateralDiffM,
        };

        c.reasons.push({
          category: 'line',
          distanceM: peakZone.peakDistanceM || peakZone.startDistanceM,
          startDistanceM: mergedLz.startDistanceM,
          endDistanceM: mergedLz.endDistanceM,
          cornerName: peakZone.cornerName || undefined,
          lineZone: mergedLz,
        });
      }
    }

    // 3. Attribute Top Speed difference within each cluster
    const minTopSpeedDiffKmh = dimSmallDiffs ? 2.0 : 1.0;
    for (const c of rawClusters) {
      if (c.maxSpeedAKmh > 2 && c.maxSpeedBKmh > 2) {
        const diffKmh = c.maxSpeedBKmh - c.maxSpeedAKmh;
        if (
          Math.abs(diffKmh) >= minTopSpeedDiffKmh ||
          (c.reasons.length === 0 && Math.abs(diffKmh) >= 0.2)
        ) {
          c.reasons.push({
            category: 'topspeed',
            distanceM: c.peakSpeedDistM,
            startDistanceM: c.startDistanceM,
            endDistanceM: c.endDistanceM,
            topSpeedDiff: {
              maxSpeedAKmh: c.maxSpeedAKmh,
              maxSpeedBKmh: c.maxSpeedBKmh,
              diffKmh,
              peakDistM: c.peakSpeedDistM,
            },
          });
        }
      }
    }

    // 4. Gently enclose matched event distances within non-overlapping range boundaries
    rawClusters.sort((a, b) => a.startDistanceM - b.startDistanceM);
    for (let idx = 0; idx < rawClusters.length; idx++) {
      const c = rawClusters[idx];
      const minBound = idx > 0 ? rawClusters[idx - 1].endDistanceM + 1.0 : 0.0;
      const maxBound =
        idx < rawClusters.length - 1
          ? rawClusters[idx + 1].startDistanceM - 1.0
          : totalLen;
      for (const r of c.reasons) {
        if (r.category !== 'topspeed' && r.category !== 'line') {
          c.startDistanceM = Math.max(
            minBound,
            Math.min(c.startDistanceM, r.distanceM - 2.0)
          );
          c.endDistanceM = Math.min(
            maxBound,
            Math.max(c.endDistanceM, r.distanceM + 2.0)
          );
        }
      }
    }

    // 4b. Enrich corner names from trackAnnotations when available
    if (timeSeries.length > 0 && (trackAnnotations || []).length > 0) {
      for (const a of trackAnnotations) {
        if (a.type !== 'corner' || !a.name) continue;
        const cPt = a.apex || a.entry || a.location;
        if (!cPt) continue;
        let bestDist = 42.0;
        let bestTrackDistM = -1;
        for (const ts of timeSeries) {
          const d = haversineDistanceM(cPt, { lat: ts.lat, lng: ts.lng });
          if (d < bestDist) {
            bestDist = d;
            bestTrackDistM = ts.distanceM;
          }
        }
        if (bestTrackDistM >= 0) {
          for (const c of rawClusters) {
            if (
              bestTrackDistM >= c.startDistanceM - 15 &&
              bestTrackDistM <= c.endDistanceM + 15 &&
              !c.cornerNames.includes(a.name)
            ) {
              c.cornerNames.push(a.name);
            }
          }
        }
      }
    }

    // 5. Order reasons inside each cluster in natural driving phase order and build final clusters
    const reasonOrder: Record<DiffReasonCategory, number> = {
      braking: 1,
      line: 2,
      acceleration: 3,
      upshift: 4,
      topspeed: 5,
    };

    const result: DiffRangeCluster[] = rawClusters
      .filter(c => c.reasons.length > 0)
      .map((c, idx) => {
        c.reasons.sort((a, b) => reasonOrder[a.category] - reasonOrder[b.category]);
        const uniqueCorners = [...new Set(c.cornerNames.filter(Boolean))];
        const cornerLabel =
          uniqueCorners.length === 0
            ? ''
            : uniqueCorners.length === 1
            ? uniqueCorners[0]
            : `${uniqueCorners[0]} – ${uniqueCorners[uniqueCorners.length - 1]}`;
        const startM = Math.round(c.startDistanceM);
        const endM = Math.max(startM + 1, Math.round(c.endDistanceM));
        return {
          key: `range-${idx}-${startM}-${endM}`,
          startDistanceM: c.startDistanceM,
          endDistanceM: c.endDistanceM,
          midDistanceM: (c.startDistanceM + c.endDistanceM) * 0.5,
          cornerName: cornerLabel,
          timeDiffMs: c.timeDiffMs,
          avgSpeedAKmh: c.avgSpeedAKmh,
          avgSpeedBKmh: c.avgSpeedBKmh,
          maxSpeedAKmh: c.maxSpeedAKmh,
          maxSpeedBKmh: c.maxSpeedBKmh,
          reasons: c.reasons,
        };
      });

    result.sort((a, b) => Math.abs(b.timeDiffMs) - Math.abs(a.timeDiffMs));
    return result;
  }, [
    analysisResult,
    effectiveTotalLengthM,
    timeSeries,
    speedsA,
    speedsB,
    allDiffItems,
    dimSmallDiffs,
    trackAnnotations,
  ]);

  const filteredRangeClusters = useMemo(() => {
    return allRangeClusters.filter(cluster => {
      if (!doesRangeClusterOverlapAnchors(cluster, anchors, effectiveTotalLengthM)) {
        return false;
      }
      if (diffFilter !== 'all') {
        return cluster.reasons.some(r => r.category === diffFilter);
      }
      return true;
    });
  }, [allRangeClusters, anchors, effectiveTotalLengthM, diffFilter]);

  const categoryCounts = useMemo(() => {
    const counts: Record<DiffFilterCategory, number> = {
      all: 0,
      line: 0,
      braking: 0,
      acceleration: 0,
      upshift: 0,
      topspeed: 0,
    };
    for (const cluster of allRangeClusters) {
      if (!doesRangeClusterOverlapAnchors(cluster, anchors, effectiveTotalLengthM)) {
        continue;
      }
      counts.all++;
      const cats = new Set(cluster.reasons.map(r => r.category));
      for (const cat of cats) {
        counts[cat]++;
      }
    }
    return counts;
  }, [allRangeClusters, anchors, effectiveTotalLengthM]);

  const hasShape = !!(
    trackShape &&
    trackShape.centerline &&
    trackShape.centerline.length >= 3
  );

  const lapsA = analysisResult?.groupA?.laps || [];
  const lapsB = analysisResult?.groupB?.laps || [];
  const refTrack =
    analysisResult?.referenceTrack && analysisResult.referenceTrack.length > 0
      ? analysisResult.referenceTrack
      : analysisResult?.groupA?.referenceTrack &&
        analysisResult.groupA.referenceTrack.length > 0
      ? analysisResult.groupA.referenceTrack
      : analysisResult?.groupB?.referenceTrack || [];

  const midLineGeo = useMemo<GeoCoord[]>(() => {
    if (hasShape && trackShape?.centerline && trackShape.centerline.length >= 3) {
      const trackWidthM = trackShape.trackWidthM || 8.0;
      const samples = sampleClosedSpline(trackShape.centerline, trackWidthM, 30);
      if (samples.length > 0) {
        const closingDist = haversineDistanceM(
          samples[samples.length - 1].point,
          samples[0].point
        );
        const totalLen = samples[samples.length - 1].distanceM + closingDist;
        const finishAnnot = (trackAnnotations || []).find(
          a => a.type === 'finish_line'
        );
        let finishDist = 0.0;
        if (finishAnnot?.location) {
          const fc = findClosestParametricT(finishAnnot.location, samples);
          finishDist = samples[fc.index]?.distanceM ?? 0.0;
        }
        const numStations = 200;
        const pts: GeoCoord[] = new Array(numStations);
        for (let k = 0; k < numStations; k++) {
          const absD = (finishDist + (k / numStations) * totalLen) % totalLen;
          const s = getSplineSampleAtDistanceM(absD, samples, totalLen);
          pts[k] = { lat: s.point.lat, lng: s.point.lng };
        }
        return pts;
      }
    }
    const lineA = analysisResult?.medianLineA || [];
    const lineB = analysisResult?.medianLineB || [];
    const n = Math.min(lineA.length, lineB.length);
    if (n >= 3) {
      const pts: GeoCoord[] = new Array(n);
      for (let k = 0; k < n; k++) {
        pts[k] = {
          lat: (lineA[k].lat + lineB[k].lat) * 0.5,
          lng: (lineA[k].lng + lineB[k].lng) * 0.5,
        };
      }
      return pts;
    }
    if (trackShape?.segments && trackShape.segments.length >= 2) {
      return trackShape.segments.map(s => ({ lat: s.lat, lng: s.lng }));
    }
    return refTrack.map(s => ({
      lat: s.latitudeRaw / 1e6,
      lng: s.longitudeRaw / 1e6,
    }));
  }, [
    hasShape,
    trackShape,
    trackAnnotations,
    analysisResult?.medianLineA,
    analysisResult?.medianLineB,
    refTrack,
  ]);

  const mapAnchor = useMemo<GeoCoord>(() => {
    if (trackShape?.centerline && trackShape.centerline.length > 0) {
      return {
        lat: trackShape.centerline[0].lat,
        lng: trackShape.centerline[0].lng,
      };
    }
    if (refTrack.length > 0) {
      return {
        lat: refTrack[0].latitudeRaw / 1e6,
        lng: refTrack[0].longitudeRaw / 1e6,
      };
    }
    if (midLineGeo.length > 0) {
      return midLineGeo[0];
    }
    return { lat: 0, lng: 0 };
  }, [trackShape, refTrack, midLineGeo]);

  const getGeoAtProgress = useCallback(
    (s: number): GeoCoord => {
      const n = midLineGeo.length;
      if (n === 0) return { lat: 0, lng: 0 };
      const kFloat = Math.max(0, Math.min(n - 1, s * n));
      const k0 = Math.floor(kFloat);
      const k1 = (k0 + 1) % n;
      const u = kFloat - k0;
      const p0 = midLineGeo[k0];
      const p1 = midLineGeo[k1];
      return {
        lat: p0.lat + u * (p1.lat - p0.lat),
        lng: p0.lng + u * (p1.lng - p0.lng),
      };
    },
    [midLineGeo]
  );

  const getProgressFromMetric = useCallback(
    (metricPt: Point2D, anchorCoord: GeoCoord): number => {
      if (midLineGeo.length === 0) return 0;
      const metricLine = midLineGeo.map(p =>
        projectToLocalMeters(p, anchorCoord)
      );
      const { progressIndex } = findClosestPointOnPolyline(
        metricPt,
        metricLine
      );
      return Math.max(0, Math.min(1, progressIndex / metricLine.length));
    },
    [midLineGeo]
  );

  const hitTestInteractive = useCallback((canvasPt: Point2D) => {
    const coords = anchorCanvasCoordsRef.current;
    if (!coords) return null;
    if (Math.hypot(canvasPt.x - coords.p0.x, canvasPt.y - coords.p0.y) <= 18) {
      return 'anchor-0';
    }
    if (Math.hypot(canvasPt.x - coords.p1.x, canvasPt.y - coords.p1.y) <= 18) {
      return 'anchor-1';
    }
    return null;
  }, []);

  const onDragHandle = useCallback(
    (handleId: string, cursorMetric: Point2D) => {
      if (!anchors) return;
      const newS = getProgressFromMetric(cursorMetric, mapAnchor);
      setAnchors(prev => {
        if (!prev) return null;
        if (handleId === 'anchor-0') return [newS, prev[1]];
        if (handleId === 'anchor-1') return [prev[0], newS];
        return prev;
      });
    },
    [anchors, mapAnchor, getProgressFromMetric]
  );

  const placeAnchorsAtProgress = useCallback(
    (clickedS: number) => {
      if (!anchors) {
        let s1 = clickedS;
        let s2 = Math.min(1.0, s1 + 0.15);
        if (s2 - s1 < 0.05) {
          s1 = Math.max(0.0, s2 - 0.15);
        }
        const n = midLineGeo.length;
        if (n > 0) {
          s1 = Math.round(s1 * n) / n;
          s2 = Math.round(s2 * n) / n;
        }
        setAnchors([Math.min(s1, s2), Math.max(s1, s2)]);
      }
    },
    [anchors, midLineGeo.length]
  );

  const handleTrackClick = useCallback(
    (_canvasPt: Point2D, metricPt: Point2D) => {
      const clickedS = getProgressFromMetric(metricPt, mapAnchor);
      placeAnchorsAtProgress(clickedS);
    },
    [mapAnchor, getProgressFromMetric, placeAnchorsAtProgress]
  );

  // Fetch backend-computed segment statistics when anchor bounds change
  useEffect(() => {
    if (!anchors) {
      setSegmentResult(null);
      return;
    }
    const effectiveTrackId = trackId || trackShape?.trackId;
    if (!effectiveTrackId || !isSideAReady || !isSideBReady) return;

    let cancelled = false;
    const timer = setTimeout(() => {
      const req: AnalyzeAggregationDiffRequest = {
        trackId: effectiveTrackId,
        sessionIdsA: modeA === 'sessions' ? [...selectedIdsA] : [],
        sessionIdsB: modeB === 'sessions' ? [...selectedIdsB] : [],
        lapA:
          modeA === 'lap' && lapA
            ? { sessionId: lapA.sessionId, lapNumber: lapA.lapNumber }
            : undefined,
        lapB:
          modeB === 'lap' && lapB
            ? { sessionId: lapB.sessionId, lapNumber: lapB.lapNumber }
            : undefined,
        startProgress: Math.min(anchors[0], anchors[1]),
        endProgress: Math.max(anchors[0], anchors[1]),
      };
      rpc<AnalyzeAggregationDiffResponse>('AnalyzeAggregationDiff', req)
        .then(res => {
          if (!cancelled) setSegmentResult(res);
        })
        .catch(err => {
          console.warn('Failed to evaluate diff segment:', err);
        });
    }, 80);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    anchors,
    trackId,
    trackShape?.trackId,
    isSideAReady,
    isSideBReady,
    modeA,
    modeB,
    selectedIdsA,
    selectedIdsB,
    lapA,
    lapB,
  ]);

  const stationAdvantages = useMemo(() => {
    if (timeSeries.length === 0) return undefined;
    return timeSeries.map(ts =>
      metric === 'time'
        ? -ts.segDiffAMs
        : metric === 'speed'
          ? ts.speedDiffKmh
          : metric === 'rpm'
            ? ts.rpmDiff
            : ts.accelDiff
    );
  }, [timeSeries, metric]);

  const fallbackTimeStats = useMemo<LapDiffMetricStats | undefined>(() => {
    if (timeSeries.length < 2) return undefined;
    const sMin = anchors ? Math.min(anchors[0], anchors[1]) : 0;
    const sMax = anchors ? Math.max(anchors[0], anchors[1]) : 1;
    let sumAbs = 0;
    let maxAbs = 0;
    let aBetter = 0;
    let bBetter = 0;
    let count = 0;
    for (let i = 1; i < timeSeries.length; i++) {
      const p = timeSeries[i];
      if (anchors && (p.progress < sMin || p.progress > sMax)) continue;
      const absD = Math.abs(p.segDiffAMs);
      sumAbs += absD;
      if (absD > maxAbs) maxAbs = absD;
      if (p.segDiffAMs < -0.01) aBetter++;
      else if (p.segDiffAMs > 0.01) bBetter++;
      count++;
    }
    if (count === 0) return undefined;
    return {
      avgAbsDiff: sumAbs / count,
      maxAbsDiff: maxAbs,
      pctABetter: Math.round((aBetter / count) * 100),
      pctBBetter: Math.round((bBetter / count) * 100),
    };
  }, [timeSeries, anchors]);

  const stats = useMemo(() => {
    const metricStats =
      metric === 'time'
        ? (activeResult?.timeStats ?? fallbackTimeStats)
        : metric === 'speed'
          ? activeResult?.speedStats
          : metric === 'rpm'
            ? activeResult?.rpmStats
            : activeResult?.accelStats;
    return formatLapDiffStats(metric, viewLap, metricStats);
  }, [metric, viewLap, activeResult, fallbackTimeStats]);

  const updateHoverAtProgress = useCallback(
    (sHover: number | null) => {
      if (sHover === null) {
        setHoveredProgress(null);
        setHoverInfo(null);
        return;
      }

      setHoveredProgress(sHover);

      if (points.length === 0) return;

      const ptIdx = Math.min(points.length - 1, Math.max(0, Math.round(sHover * (points.length - 1))));
      const pt = points[ptIdx];
      const ts = timeSeries[ptIdx];

      const sign = viewLap === 'A' ? 1 : -1;
      const segTimeAMs = ts?.segTimeAMs ?? pt.lapA.segmentTimeMs ?? 0;
      const segTimeBMs = ts?.segTimeBMs ?? pt.lapB.segmentTimeMs ?? 0;
      const cumTimeAMs = ts?.cumTimeAMs ?? pt.lapA.cumulativeTimeMs ?? 0;
      const cumTimeBMs = ts?.cumTimeBMs ?? pt.lapB.cumulativeTimeMs ?? 0;
      const segmentTimeDiffMs = (ts?.segDiffAMs ?? (segTimeAMs - segTimeBMs)) * sign;
      const cumulativeTimeDiffMs = (ts?.cumDiffAMs ?? (cumTimeAMs - cumTimeBMs)) * sign;

      const vA =
        metric === 'time'
          ? segTimeAMs
          : metric === 'speed'
            ? pt.lapA.speedKmh
            : metric === 'rpm'
              ? pt.lapA.rpm
              : pt.lapA.acceleration;
      const vB =
        metric === 'time'
          ? segTimeBMs
          : metric === 'speed'
            ? pt.lapB.speedKmh
            : metric === 'rpm'
              ? pt.lapB.rpm
              : pt.lapB.acceleration;
      const diff = viewLap === 'A' ? vA - vB : vB - vA;

      setHoverInfo({
        sampleIndex: ptIdx,
        segmentIndex: ptIdx,
        latRaw: Number(pt.latitudeRaw),
        lonRaw: Number(pt.longitudeRaw),
        valueA: vA,
        valueB: vB,
        diff,
        unit: getMetricUnit(metric),
        segmentTimeDiffMs,
        cumulativeTimeDiffMs,
        segTimeAMs,
        segTimeBMs,
        cumTimeAMs,
        cumTimeBMs,
      });
    },
    [points, timeSeries, metric, viewLap]
  );

  useEffect(() => {
    if (hoveredProgress !== null) {
      updateHoverAtProgress(hoveredProgress);
    }
  }, [metric, viewLap, hoveredProgress, updateHoverAtProgress]);

  const segmentComparison = useMemo(() => {
    if (!analysisResult) return null;
    const n = timeSeries.length;
    const isSegment = anchors !== null;
    const sMin = isSegment ? Math.min(anchors[0], anchors[1]) : 0.0;
    const sMax = isSegment ? Math.max(anchors[0], anchors[1]) : 1.0;

    if (n < 3) {
      const diffMs = Math.abs(fullLapAMs - fullLapBMs);
      const fasterGroup: 'A' | 'B' | 'tie' =
        fullLapAMs > 0 && fullLapBMs > 0
          ? fullLapAMs < fullLapBMs
            ? 'A'
            : fullLapBMs < fullLapAMs
            ? 'B'
            : 'tie'
          : 'tie';
      return {
        isSegment,
        startPct: Math.round(sMin * 100),
        endPct: Math.round(sMax * 100),
        startDistM: Math.round(sMin * effectiveTotalLengthM),
        endDistM: Math.round(sMax * effectiveTotalLengthM),
        timeAMs: fullLapAMs,
        timeBMs: fullLapBMs,
        diffMs,
        fasterGroup,
        avgSpeedAKmh: 0,
        avgSpeedBKmh: 0,
      };
    }

    let segTimeAMs = 0;
    let segTimeBMs = 0;
    let sumSpdA = 0;
    let sumSpdB = 0;
    let weightSum = 0;

    if (!isSegment) {
      segTimeAMs = fullLapAMs > 0 ? fullLapAMs : timeSeries[n - 1].cumTimeAMs;
      segTimeBMs = fullLapBMs > 0 ? fullLapBMs : timeSeries[n - 1].cumTimeBMs;
      for (let k = 0; k < n; k++) {
        sumSpdA += speedsA[k] || 0;
        sumSpdB += speedsB[k] || 0;
      }
      weightSum = n;
    } else {
      const kStartFloat = sMin * n;
      const kEndFloat = sMax * n;
      for (let k = 0; k < n; k++) {
        const overlapStart = Math.max(k, kStartFloat);
        const overlapEnd = Math.min(k + 1, kEndFloat);
        const frac = overlapEnd - overlapStart;
        if (frac > 0) {
          segTimeAMs += timeSeries[k].segTimeAMs * frac;
          segTimeBMs += timeSeries[k].segTimeBMs * frac;
          sumSpdA += (speedsA[k] || 0) * frac;
          sumSpdB += (speedsB[k] || 0) * frac;
          weightSum += frac;
        }
      }
    }

    const diffMs = Math.abs(segTimeAMs - segTimeBMs);
    let fasterGroup: 'A' | 'B' | 'tie' = 'tie';
    if (diffMs >= 5) {
      fasterGroup = segTimeAMs < segTimeBMs ? 'A' : 'B';
    }

    return {
      isSegment,
      startPct: Math.round(sMin * 100),
      endPct: Math.round(sMax * 100),
      startDistM: Math.round(sMin * effectiveTotalLengthM),
      endDistM: Math.round(sMax * effectiveTotalLengthM),
      timeAMs: segTimeAMs,
      timeBMs: segTimeBMs,
      diffMs,
      fasterGroup,
      avgSpeedAKmh: weightSum > 0 ? sumSpdA / weightSum : 0,
      avgSpeedBKmh: weightSum > 0 ? sumSpdB / weightSum : 0,
    };
  }, [analysisResult, timeSeries, speedsA, speedsB, fullLapAMs, fullLapBMs, effectiveTotalLengthM, anchors]);

  const timeDiff = useMemo(() => {
    const totalDiffMs = Math.abs(fullLapAMs - fullLapBMs);
    const fasterFull: 'A' | 'B' | 'tie' =
      fullLapAMs > 0 && fullLapBMs > 0
        ? fullLapAMs < fullLapBMs
          ? 'A'
          : fullLapBMs < fullLapAMs
            ? 'B'
            : 'tie'
        : 'tie';
    const segA = activeResult?.segmentLapATimeMs ?? segmentComparison?.timeAMs;
    const segB = activeResult?.segmentLapBTimeMs ?? segmentComparison?.timeBMs;
    const segDiff = activeResult?.segmentTimeDiffMs ?? segmentComparison?.diffMs;
    const segFaster = activeResult?.segmentFasterLap || segmentComparison?.fasterGroup || 'tie';
    return formatLapDiffTimeDiff(
      fullLapAMs,
      fullLapBMs,
      totalDiffMs,
      fasterFull,
      anchors !== null,
      segA,
      segB,
      segDiff,
      segFaster
    );
  }, [fullLapAMs, fullLapBMs, activeResult, segmentComparison, anchors]);

  // Compute geographic bounds for automatic camera zoom when a Key Difference range is selected
  const focusedZoneBounds = useMemo<Array<{ lat: number; lng: number }> | null>(() => {
    if (!focusedDiffKey || midLineGeo.length === 0) return null;
    const totalLen = Math.max(1.0, effectiveTotalLengthM);

    const focusedCluster = allRangeClusters.find(c => c.key === focusedDiffKey);
    const focusedItem = !focusedCluster
      ? allDiffItems.find(it => it.key === focusedDiffKey)
      : undefined;
    if (!focusedCluster && !focusedItem) return null;

    let startD = 0;
    let endD = 20;

    if (focusedCluster) {
      startD = focusedCluster.startDistanceM;
      endD = focusedCluster.endDistanceM;
    } else if (focusedItem) {
      startD = focusedItem.distanceM - 10;
      endD = focusedItem.distanceM + 10;
      if (focusedItem.lineZone) {
        startD = focusedItem.lineZone.startDistanceM;
        endD = focusedItem.lineZone.endDistanceM;
      } else if (focusedItem.segmentDiff) {
        const sd = focusedItem.segmentDiff;
        const dA = sd.segmentA ? getSegDistances(sd.segmentA, totalLen) : null;
        const dB = sd.segmentB ? getSegDistances(sd.segmentB, totalLen) : null;
        if (sd.matchStatus === 'only_a' && dA) {
          startD = dA.minDistM;
          endD = dA.maxDistM;
        } else if (sd.matchStatus === 'only_b' && dB) {
          startD = dB.minDistM;
          endD = dB.maxDistM;
        } else if (dA && dB) {
          startD = Math.min(dA.minDistM, dB.minDistM);
          endD = Math.max(dA.maxDistM, dB.maxDistM);
        }
      }
    }

    // Pad with surrounding context so the corner and approach/exit are clearly visible
    const midD = (startD + endD) * 0.5;
    const halfSpan = Math.max(28.0, (endD - startD) * 0.5 + 18.0);
    const padStart = midD - halfSpan;
    const padEnd = midD + halfSpan;

    const pts: Array<{ lat: number; lng: number }> = [];
    const numSamples = 24;
    for (let i = 0; i <= numSamples; i++) {
      const d = padStart + (i / numSamples) * (padEnd - padStart);
      const s = (((d % totalLen) + totalLen) % totalLen) / totalLen;
      pts.push(getGeoAtProgress(s));
    }
    return pts;
  }, [
    focusedDiffKey,
    allRangeClusters,
    allDiffItems,
    midLineGeo.length,
    effectiveTotalLengthM,
    getGeoAtProgress,
  ]);

  // Canvas overlay renderer for the InteractiveTrackMap
  const renderOverlay = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      { toCanvas, anchor, viewport, width, height }: OverlayUtils
    ) => {
      if (!analysisResult) return;

      const project = (latRaw: number, lonRaw: number) => {
        const p = projectToLocalMeters(
          { lat: latRaw / 1e6, lng: lonRaw / 1e6 },
          anchor
        );
        return toCanvas(p);
      };

      const trackWidthM = trackShape?.trackWidthM || 8.0;
      const splineSamples = hasShape
        ? sampleClosedSpline(trackShape!.centerline, trackWidthM, 30)
        : [];
      const loopClosingDist =
        splineSamples.length > 0
          ? haversineDistanceM(
              splineSamples[splineSamples.length - 1].point,
              splineSamples[0].point
            )
          : 0;
      const totalLengthM =
        splineSamples.length > 0
          ? splineSamples[splineSamples.length - 1].distanceM + loopClosingDist
          : effectiveTotalLengthM;

      const finishLineAnnot = (trackAnnotations || []).find(
        a => a.type === 'finish_line'
      );
      let finishDistM = 0.0;
      let finishClosest: ReturnType<typeof findClosestParametricT> | null = null;
      if (finishLineAnnot?.location && splineSamples.length > 0) {
        finishClosest = findClosestParametricT(
          finishLineAnnot.location,
          splineSamples
        );
        finishDistM = splineSamples[finishClosest.index]?.distanceM ?? 0.0;
      }

      // 0. Faster Segments Track Surface Highlight (rendered FIRST on the track map ribbon behind all other highlightings)
      if (showFasterSegments) {
        drawFasterSegmentsOverlay(
          ctx,
          analysisResult.medianLineA || [],
          analysisResult.medianLineB || [],
          speedsA,
          speedsB,
          anchor,
          toCanvas,
          anchors,
          hasShape && splineSamples.length > 0
            ? {
                splineSamples,
                totalLengthM,
                finishDistM,
                stationAdvantages,
              }
            : stationAdvantages
              ? {
                  stationAdvantages,
                }
              : undefined
        );
        if (finishClosest) {
          drawFinishLine(ctx, finishClosest, trackWidthM, anchor, toCanvas, 3);
        }
      }

      const focusedCluster = focusedDiffKey
        ? allRangeClusters.find(c => c.key === focusedDiffKey)
        : undefined;

      // 1. Split-Track Event Zone Diffs (Side A = inner/left half [+0.06..+0.98], Side B = outer/right half [-0.98..-0.06])
      if (showEventDiffs && hasShape && splineSamples.length > 0) {
        for (const item of allDiffItems) {
          const sd = item.segmentDiff;
          if (!sd) continue;
          if (!doesDiffItemOverlapAnchors(item, anchors, totalLengthM)) {
            continue;
          }

          const isOnlyA = sd.matchStatus === 'only_a';
          const isOnlyB = sd.matchStatus === 'only_b';
          const isFocused = focusedCluster
            ? focusedCluster.reasons.some(r => r.segmentDiff === sd)
            : focusedDiffKey === item.key;
          const isOtherFocused = Boolean(focusedDiffKey && !isFocused);
          const isSmallDiff =
            !isOnlyA &&
            !isOnlyB &&
            Math.abs(sd.deltaMedianM || 0) < 1.5 &&
            Math.abs(item.spreadDiffM) < 2.0;

          let alphaMultiplier = 1.0;
          if (isOtherFocused) alphaMultiplier = 0.22;
          else if (dimSmallDiffs && isSmallDiff && !isFocused)
            alphaMultiplier = 0.18;

          const isBraking =
            sd.eventType === 'AGGREGATION_EVENT_TYPE_BRAKING' ||
            sd.eventType === 1;
          const isAccel =
            sd.eventType === 'AGGREGATION_EVENT_TYPE_ACCELERATION' ||
            sd.eventType === 2;

          const baseRgb = isBraking
            ? '239, 68, 68'
            : isAccel
            ? '34, 197, 94'
            : '56, 189, 248';
          const strokeHex = isBraking
            ? '#ef4444'
            : isAccel
            ? '#22c55e'
            : '#38bdf8';

          const whiskerFill = `rgba(${baseRgb}, ${(0.16 * alphaMultiplier).toFixed(3)})`;
          const boxFill = `rgba(${baseRgb}, ${(0.38 * alphaMultiplier).toFixed(3)})`;

          let medMidA: { midX: number; midY: number } | null = null;
          let medMidB: { midX: number; midY: number } | null = null;

          // Side A on left/inner half of track: lateral fraction [0.06, 0.98]
          if (sd.segmentA && sd.segmentA.sampleCount >= 1) {
            const dA = getSegDistances(sd.segmentA, totalLengthM);
            if (sd.segmentA.sampleCount >= 2) {
              drawLateralBandPolygon(
                dA.minDistM,
                dA.q1DistM,
                finishDistM,
                totalLengthM,
                splineSamples,
                project,
                ctx,
                0.06,
                0.98,
                whiskerFill
              );
              drawLateralBandPolygon(
                dA.q3DistM,
                dA.maxDistM,
                finishDistM,
                totalLengthM,
                splineSamples,
                project,
                ctx,
                0.06,
                0.98,
                whiskerFill
              );
              drawLateralBandPolygon(
                dA.q1DistM,
                dA.q3DistM,
                finishDistM,
                totalLengthM,
                splineSamples,
                project,
                ctx,
                0.06,
                0.98,
                boxFill,
                strokeHex,
                isFocused ? 1.8 : 1.1,
                isOnlyA
              );
            } else {
              drawLateralBandPolygon(
                dA.medDistM - 1.5,
                dA.medDistM + 1.5,
                finishDistM,
                totalLengthM,
                splineSamples,
                project,
                ctx,
                0.06,
                0.98,
                boxFill,
                strokeHex,
                isFocused ? 1.8 : 1.1,
                isOnlyA
              );
            }
            medMidA = drawLateralTick(
              dA.medDistM,
              finishDistM,
              totalLengthM,
              splineSamples,
              project,
              ctx,
              0.06,
              1.02,
              '#ef4444',
              isFocused ? 2.8 : 2.1
            );
          }

          // Side B on right/outer half of track: lateral fraction [-0.98, -0.06]
          if (sd.segmentB && sd.segmentB.sampleCount >= 1) {
            const dB = getSegDistances(sd.segmentB, totalLengthM);
            if (sd.segmentB.sampleCount >= 2) {
              drawLateralBandPolygon(
                dB.minDistM,
                dB.q1DistM,
                finishDistM,
                totalLengthM,
                splineSamples,
                project,
                ctx,
                -0.06,
                -0.98,
                whiskerFill
              );
              drawLateralBandPolygon(
                dB.q3DistM,
                dB.maxDistM,
                finishDistM,
                totalLengthM,
                splineSamples,
                project,
                ctx,
                -0.06,
                -0.98,
                whiskerFill
              );
              drawLateralBandPolygon(
                dB.q1DistM,
                dB.q3DistM,
                finishDistM,
                totalLengthM,
                splineSamples,
                project,
                ctx,
                -0.06,
                -0.98,
                boxFill,
                strokeHex,
                isFocused ? 1.8 : 1.1,
                isOnlyB
              );
            } else {
              drawLateralBandPolygon(
                dB.medDistM - 1.5,
                dB.medDistM + 1.5,
                finishDistM,
                totalLengthM,
                splineSamples,
                project,
                ctx,
                -0.06,
                -0.98,
                boxFill,
                strokeHex,
                isFocused ? 1.8 : 1.1,
                isOnlyB
              );
            }
            medMidB = drawLateralTick(
              dB.medDistM,
              finishDistM,
              totalLengthM,
              splineSamples,
              project,
              ctx,
              -0.06,
              -1.02,
              '#3b82f6',
              isFocused ? 2.8 : 2.1
            );
          }

          // Bridge connector between Side A median and Side B median
          if (medMidA && medMidB && alphaMultiplier > 0.3) {
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(medMidA.midX, medMidA.midY);
            ctx.lineTo(medMidB.midX, medMidB.midY);
            ctx.strokeStyle = isFocused
              ? '#ffffff'
              : 'rgba(255, 255, 255, 0.75)';
            ctx.lineWidth = isFocused ? 2.2 : 1.4;
            ctx.stroke();
            ctx.restore();
          }
        }
      }

      // 2. Differential Two-Color GPS Driving Line Heat Map
      if (showDiffHeatmap) {
        drawGpsLineDiffHeatmap(
          ctx,
          lapsA,
          lapsB,
          anchor,
          toCanvas,
          viewport.scale,
          width,
          height,
          anchors,
          hasShape && splineSamples.length > 0
            ? {
                splineSamples,
                totalLengthM,
                finishDistM,
              }
            : undefined
        );
      }

      // 3. Draggable Segment Anchor Handles on Track Line
      if (anchors) {
        const geo0 = getGeoAtProgress(anchors[0]);
        const geo1 = getGeoAtProgress(anchors[1]);
        const cp0 = toCanvas(projectToLocalMeters(geo0, anchor));
        const cp1 = toCanvas(projectToLocalMeters(geo1, anchor));
        anchorCanvasCoordsRef.current = { p0: cp0, p1: cp1 };

        const drawAnchor = (p: Point2D) => {
          ctx.save();
          ctx.beginPath();
          ctx.arc(p.x, p.y, 14, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(124, 92, 252, 0.25)';
          ctx.fill();
          ctx.beginPath();
          ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
          ctx.fillStyle = '#7c5cfc';
          ctx.fill();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.restore();
        };

        drawAnchor(cp0);
        drawAnchor(cp1);
      } else {
        anchorCanvasCoordsRef.current = null;
      }

      // 4. Focused Difference Highlight Overlay (pulses/frames the selected range on the map)
      if (focusedDiffKey && hasShape && splineSamples.length > 0) {
        const focusedItem = !focusedCluster
          ? allDiffItems.find(it => it.key === focusedDiffKey)
          : undefined;
        if (focusedCluster || focusedItem) {
          let startD = 0;
          let endD = 16;
          let midD = 8;
          let badgeText = '';

          if (focusedCluster) {
            startD = focusedCluster.startDistanceM;
            endD = focusedCluster.endDistanceM;
            midD = focusedCluster.midDistanceM;
            const rangeStr = `${Math.round(startD)}m–${Math.round(endD)}m`;
            badgeText = focusedCluster.cornerName
              ? `${focusedCluster.cornerName} (${rangeStr})`
              : rangeStr;
            if (Math.abs(focusedCluster.timeDiffMs) >= 5) {
              const secStr = `${(Math.abs(focusedCluster.timeDiffMs) / 1000).toFixed(2)}s`;
              const fasterGroup =
                focusedCluster.timeDiffMs < 0 ? groupBLabel : groupALabel;
              const timeLabel = t('aggregationdiff.estTimeGained', {
                group: fasterGroup,
                time: secStr,
              });
              badgeText = `${badgeText} · ${timeLabel}`;
            }
          } else if (focusedItem) {
            startD = focusedItem.distanceM - 8;
            endD = focusedItem.distanceM + 8;
            midD = focusedItem.distanceM;

            if (focusedItem.lineZone) {
              startD = focusedItem.lineZone.startDistanceM;
              endD = focusedItem.lineZone.endDistanceM;
              badgeText = `${focusedItem.lineZone.maxLateralDiffM.toFixed(1)}m line diff`;
            } else if (focusedItem.segmentDiff) {
              const sd = focusedItem.segmentDiff;
              const dA = sd.segmentA
                ? getSegDistances(sd.segmentA, totalLengthM)
                : null;
              const dB = sd.segmentB
                ? getSegDistances(sd.segmentB, totalLengthM)
                : null;
              if (sd.matchStatus === 'only_a' && dA) {
                startD = dA.q1DistM;
                endD = dA.q3DistM;
                badgeText = `${groupALabel} ONLY`;
              } else if (sd.matchStatus === 'only_b' && dB) {
                startD = dB.q1DistM;
                endD = dB.q3DistM;
                badgeText = `${groupBLabel} ONLY`;
              } else if (dA && dB) {
                startD = Math.min(dA.q1DistM, dB.q1DistM);
                endD = Math.max(dA.q3DistM, dB.q3DistM);
                const sign = (sd.deltaMedianM || 0) > 0 ? '+' : '';
                badgeText = `Δ ${sign}${(sd.deltaMedianM || 0).toFixed(1)}m`;
              }
            }

            if (
              focusedItem.estimatedTimeDiffMs !== undefined &&
              Math.abs(focusedItem.estimatedTimeDiffMs) >= 5
            ) {
              const secStr = `${(Math.abs(focusedItem.estimatedTimeDiffMs) / 1000).toFixed(2)}s`;
              const fasterGroup =
                focusedItem.estimatedTimeDiffMs < 0 ? groupBLabel : groupALabel;
              const timeLabel = t('aggregationdiff.estTimeGained', {
                group: fasterGroup,
                time: secStr,
              });
              badgeText = badgeText ? `${badgeText} · ${timeLabel}` : timeLabel;
            }
          }

          drawLateralBandPolygon(
            startD - 3,
            endD + 3,
            finishDistM,
            totalLengthM,
            splineSamples,
            project,
            ctx,
            1.35,
            -1.35,
            'rgba(250, 204, 21, 0.14)',
            '#facc15',
            2.2,
            false
          );

          const absMid =
            ((finishDistM + midD) % totalLengthM + totalLengthM) % totalLengthM;
          const midSample = getSplineSampleAtDistanceM(
            absMid,
            splineSamples,
            totalLengthM
          );
          const calloutPt = getOffsetPoint(
            midSample,
            (midSample.widthM || 8.0) * 1.35
          );
          const cp = project(
            Math.round(calloutPt.lat * 1e6),
            Math.round(calloutPt.lng * 1e6)
          );

          if (badgeText) {
            ctx.save();
            ctx.font =
              'bold 11px ui-monospace, SFMono-Regular, Menlo, monospace';
            const textW = ctx.measureText(badgeText).width;
            const padX = 7;
            const boxW = textW + padX * 2;
            const boxH = 20;
            ctx.fillStyle = 'rgba(15, 23, 42, 0.92)';
            ctx.strokeStyle = '#facc15';
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.roundRect(cp.x - boxW / 2, cp.y - boxH / 2, boxW, boxH, 6);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = '#fef08a';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(badgeText, cp.x, cp.y + 0.5);
            ctx.restore();
          }
        }
      }

      // 5. Hovered Line Station Synchronized Markers (Side A = Red dot, Side B = Blue dot)
      if (hoveredItem && (hoveredItem.pointA || hoveredItem.pointB)) {
        const cA = hoveredItem.pointA
          ? toCanvas(projectToLocalMeters(hoveredItem.pointA, anchor))
          : null;
        const cB = hoveredItem.pointB
          ? toCanvas(projectToLocalMeters(hoveredItem.pointB, anchor))
          : null;

        ctx.save();
        if (cA && cB) {
          ctx.beginPath();
          ctx.moveTo(cA.x, cA.y);
          ctx.lineTo(cB.x, cB.y);
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
          ctx.lineWidth = 1.6;
          ctx.setLineDash([3, 2]);
          ctx.stroke();
          ctx.setLineDash([]);
        }
        if (cA) {
          ctx.beginPath();
          ctx.arc(cA.x, cA.y, 5, 0, Math.PI * 2);
          ctx.fillStyle = '#ef4444';
          ctx.fill();
          ctx.lineWidth = 1.8;
          ctx.strokeStyle = '#ffffff';
          ctx.stroke();
        }
        if (cB) {
          ctx.beginPath();
          ctx.arc(cB.x, cB.y, 5, 0, Math.PI * 2);
          ctx.fillStyle = '#3b82f6';
          ctx.fill();
          ctx.lineWidth = 1.8;
          ctx.strokeStyle = '#ffffff';
          ctx.stroke();
        }
        ctx.restore();
      } else if (hoveredProgress !== null) {
        const hGeo = getGeoAtProgress(hoveredProgress);
        const hp = toCanvas(projectToLocalMeters(hGeo, anchor));
        ctx.save();
        ctx.beginPath();
        ctx.arc(hp.x, hp.y, 7, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(hp.x, hp.y, 4, 0, Math.PI * 2);
        ctx.fillStyle = '#7c5cfc';
        ctx.fill();
        ctx.restore();
      }
    },
    [
      analysisResult,
      lapsA,
      lapsB,
      speedsA,
      speedsB,
      trackShape,
      trackAnnotations,
      hasShape,
      effectiveTotalLengthM,
      showEventDiffs,
      showDiffHeatmap,
      showFasterSegments,
      dimSmallDiffs,
      allRangeClusters,
      allDiffItems,
      focusedDiffKey,
      hoveredItem,
      hoveredProgress,
      stationAdvantages,
      groupALabel,
      groupBLabel,
      anchors,
      getGeoAtProgress,
    ]
  );

  // Hover detection over driving lines, event zone diffs, and line divergence zones
  const handleHover = useCallback(
    (
      canvasPt: { x: number; y: number } | null,
      metricPt: { x: number; y: number } | null
    ) => {
      if (!canvasPt || !metricPt || !analysisResult) {
        setHoveredItem(null);
        updateHoverAtProgress(null);
        return;
      }

      const sHover = getProgressFromMetric(metricPt, mapAnchor);
      updateHoverAtProgress(sHover);

      const splineSamples =
        trackShape?.centerline && trackShape.centerline.length >= 3
          ? sampleClosedSpline(
              trackShape.centerline,
              trackShape.trackWidthM || 8.0,
              30
            )
          : [];
      const loopClosingDist =
        splineSamples.length > 0
          ? haversineDistanceM(
              splineSamples[splineSamples.length - 1].point,
              splineSamples[0].point
            )
          : 0;
      const totalLengthM =
        splineSamples.length > 0
          ? splineSamples[splineSamples.length - 1].distanceM + loopClosingDist
          : effectiveTotalLengthM;

      const finishLineAnnot = (trackAnnotations || []).find(
        a => a.type === 'finish_line'
      );
      let finishDistM = 0.0;
      if (finishLineAnnot?.location && splineSamples.length > 0) {
        const closest = findClosestParametricT(
          finishLineAnnot.location,
          splineSamples
        );
        finishDistM = splineSamples[closest.index]?.distanceM ?? 0.0;
      }

      const anchorCoord: GeoCoord =
        trackShape?.centerline && trackShape.centerline.length > 0
          ? {
              lat: trackShape.centerline[0].lat,
              lng: trackShape.centerline[0].lng,
            }
          : refTrack.length > 0
          ? {
              lat: refTrack[0].latitudeRaw / 1e6,
              lng: refTrack[0].longitudeRaw / 1e6,
            }
          : { lat: 0, lng: 0 };

      let bestItem: UnifiedDiffItem | undefined;
      let bestItemDist = Infinity;

      if (splineSamples.length > 0) {
        for (const item of allDiffItems) {
          if (!doesDiffItemOverlapAnchors(item, anchors, totalLengthM)) {
            continue;
          }
          if (
            item.category === 'line' &&
            !showDiffHeatmap &&
            !showFasterSegments
          )
            continue;
          if (item.category !== 'line' && !showEventDiffs) continue;

          const absD =
            ((finishDistM + item.distanceM) % totalLengthM + totalLengthM) %
            totalLengthM;
          const s = getSplineSampleAtDistanceM(
            absD,
            splineSamples,
            totalLengthM
          );
          const itemMetric = projectToLocalMeters(s.point, anchorCoord);
          const dx = itemMetric.x - metricPt.x;
          const dy = itemMetric.y - metricPt.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 16 && dist < bestItemDist) {
            bestItemDist = dist;
            bestItem = item;
          }
        }
      }

      // Find closest station along the median driving lines (Side A & Side B)
      const lineA = analysisResult.medianLineA || [];
      const lineB = analysisResult.medianLineB || [];
      const stationCount = Math.max(lineA.length, lineB.length);

      let bestStationIdx = -1;
      let bestStationDist = Infinity;

      const sMin = anchors ? Math.min(anchors[0], anchors[1]) : 0;
      const sMax = anchors ? Math.max(anchors[0], anchors[1]) : 1;
      const kStart = anchors && stationCount > 1
        ? Math.max(0, Math.floor(sMin * (stationCount - 1)))
        : 0;
      const kEnd = anchors && stationCount > 1
        ? Math.min(stationCount - 1, Math.ceil(sMax * (stationCount - 1)))
        : stationCount - 1;

      for (let k = kStart; k <= kEnd; k++) {
        const ptA = lineA[k];
        const ptB = lineB[k];
        let dMin = Infinity;
        if (ptA) {
          const mA = projectToLocalMeters(ptA, anchorCoord);
          const dA = Math.hypot(mA.x - metricPt.x, mA.y - metricPt.y);
          if (dA < dMin) dMin = dA;
        }
        if (ptB) {
          const mB = projectToLocalMeters(ptB, anchorCoord);
          const dB = Math.hypot(mB.x - metricPt.x, mB.y - metricPt.y);
          if (dB < dMin) dMin = dB;
        }
        if (ptA && ptB) {
          const mA = projectToLocalMeters(ptA, anchorCoord);
          const mB = projectToLocalMeters(ptB, anchorCoord);
          const dMid = Math.hypot(
            (mA.x + mB.x) * 0.5 - metricPt.x,
            (mA.y + mB.y) * 0.5 - metricPt.y
          );
          if (dMid < dMin) dMin = dMid;
        }
        if (dMin < 16 && dMin < bestStationDist) {
          bestStationDist = dMin;
          bestStationIdx = k;
        }
      }

      if (!bestItem && bestStationIdx === -1) {
        setHoveredItem(null);
        return;
      }

      const pointA =
        bestStationIdx >= 0 && bestStationIdx < lineA.length
          ? lineA[bestStationIdx]
          : undefined;
      const pointB =
        bestStationIdx >= 0 && bestStationIdx < lineB.length
          ? lineB[bestStationIdx]
          : undefined;

      let speedAKmh =
        bestStationIdx >= 0 && bestStationIdx < speedsA.length
          ? speedsA[bestStationIdx]
          : undefined;
      let speedBKmh =
        bestStationIdx >= 0 && bestStationIdx < speedsB.length
          ? speedsB[bestStationIdx]
          : undefined;

      // Fallback: if station speeds are missing, average speedKmh from lap GPS points near cursor
      const computeFallbackGroupSpeed = (laps: typeof lapsA): number | undefined => {
        let sum = 0;
        let count = 0;
        const maxLaps = Math.min(laps.length, 25);
        for (let i = 0; i < maxLaps; i++) {
          const pts = laps[i].gpsPoints || [];
          let bestDSq = 144; // 12m radius squared
          let bestSpd = 0;
          for (const p of pts) {
            if (!p.speedKmh || p.speedKmh <= 0) continue;
            const m = projectToLocalMeters(
              { lat: p.latitudeRaw / 1e6, lng: p.longitudeRaw / 1e6 },
              anchorCoord
            );
            const dx = m.x - metricPt.x;
            const dy = m.y - metricPt.y;
            const dSq = dx * dx + dy * dy;
            if (dSq < bestDSq) {
              bestDSq = dSq;
              bestSpd = p.speedKmh;
            }
          }
          if (bestSpd > 0) {
            sum += bestSpd;
            count++;
          }
        }
        return count > 0 ? Math.round((sum / count) * 10) / 10 : undefined;
      };

      if (!speedAKmh || speedAKmh <= 0) {
        speedAKmh =
          bestItem?.lineZone?.avgSpeedAKmh && bestItem.lineZone.avgSpeedAKmh > 0
            ? bestItem.lineZone.avgSpeedAKmh
            : computeFallbackGroupSpeed(lapsA);
      }
      if (!speedBKmh || speedBKmh <= 0) {
        speedBKmh =
          bestItem?.lineZone?.avgSpeedBKmh && bestItem.lineZone.avgSpeedBKmh > 0
            ? bestItem.lineZone.avgSpeedBKmh
            : computeFallbackGroupSpeed(lapsB);
      }

      const speedDiffKmh =
        speedAKmh !== undefined &&
        speedAKmh > 0 &&
        speedBKmh !== undefined &&
        speedBKmh > 0
          ? Math.round((speedBKmh - speedAKmh) * 10) / 10
          : undefined;

      const lateralDiffM =
        pointA && pointB ? haversineDistanceM(pointA, pointB) : undefined;
      const distanceM =
        bestStationIdx >= 0 && stationCount > 0
          ? (bestStationIdx / stationCount) * totalLengthM
          : bestItem?.distanceM;

      let cornerName = bestItem?.cornerName || '';
      if (!cornerName && pointA && (trackAnnotations || []).length > 0) {
        let bestCornerDist = 35;
        for (const a of trackAnnotations) {
          if (a.type !== 'corner' || !a.name) continue;
          const cPt = a.apex || a.entry || a.location;
          if (!cPt) continue;
          const d = haversineDistanceM(pointA, cPt);
          if (d < bestCornerDist) {
            bestCornerDist = d;
            cornerName = a.name;
          }
        }
      }

      setHoveredItem({
        item: bestItem,
        x: canvasPt.x,
        y: canvasPt.y,
        pointA,
        pointB,
        speedAKmh,
        speedBKmh,
        speedDiffKmh,
        lateralDiffM,
        distanceM,
        cornerName,
      });
    },
    [
      analysisResult,
      speedsA,
      speedsB,
      trackShape,
      trackAnnotations,
      effectiveTotalLengthM,
      allDiffItems,
      anchors,
      showEventDiffs,
      showFasterSegments,
      showDiffHeatmap,
      lapsA,
      lapsB,
      refTrack,
      mapAnchor,
      getProgressFromMetric,
      updateHoverAtProgress,
    ]
  );

  const uniqueDrivers = useMemo(
    () =>
      [...new Set(sessions.map(s => s.driverName || '').filter(Boolean))].sort(),
    [sessions]
  );

  const formatSignedDistanceHeadline = (sd: AggregationSegmentDiff): string => {
    if (sd.matchStatus === 'only_a')
      return t('aggregationdiff.onlyInGroup', { group: groupALabel });
    if (sd.matchStatus === 'only_b')
      return t('aggregationdiff.onlyInGroup', { group: groupBLabel });
    const diff = sd.deltaMedianM || 0;
    const absDist = Math.abs(diff).toFixed(1);
    if (Math.abs(diff) < 0.2) return 'Δ 0.0m';
    if (diff > 0) {
      return t('aggregationdiff.laterBy', { group: groupBLabel, dist: absDist });
    }
    return t('aggregationdiff.earlierBy', { group: groupBLabel, dist: absDist });
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} isNotFound={notFound} />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <ToolHeader
        title={t('analysis.aggregationdiff.title')}
        icon={GitCompareArrows}
      />

      {/* Wizard stepper */}
      <div className="flex justify-center mb-6">
        <WizardStepper
          steps={wizardSteps}
          currentStep={step}
          onStepClick={handleStepClick}
        />
      </div>

      {/* Step 0: Choose Track */}
      {step === 0 && (
        <div className="max-w-2xl mx-auto">
          <div className="card overflow-hidden">
            <div className="px-4 py-3 border-b border-border-subtle">
              <h2 className="text-sm font-semibold text-text-primary">
                {t('gearshifting.wizardChooseTrack')}
              </h2>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {availableTracks.length > 0 ? (
                availableTracks.map(track => {
                  const isSelected = draftTrack === track;
                  return (
                    <div
                      key={track}
                      onClick={() => setDraftTrack(track)}
                      className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors border-b border-border-subtle last:border-0 group ${
                        isSelected
                          ? 'bg-accent/10 border-l-2 border-l-accent'
                          : 'hover:bg-bg-hover'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          checked={isSelected}
                          onChange={() => setDraftTrack(track)}
                          className="w-3.5 h-3.5 accent-accent cursor-pointer"
                        />
                        <span className="text-sm font-medium text-text-primary">
                          {track}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center text-text-muted text-sm">
                  {t('corner.noTracksWithCorners')}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-border-subtle flex justify-end">
              <button
                disabled={!draftTrack}
                onClick={() => {
                  if (draftTrack) {
                    const isNewTrack = draftTrack !== selectedTrack;
                    setSelectedTrack(draftTrack);
                    navigate(
                      `#/analysis/aggregation-diff/${encodeURIComponent(draftTrack)}`,
                      true
                    );
                    if (isNewTrack) {
                      setAnalysisResult(null);
                      updateWizardState(
                        {
                          step: 1,
                          selectedIdsA: [],
                          selectedIdsB: [],
                          lapA: null,
                          lapB: null,
                          focusedDiffKey: '',
                        },
                        false
                      );
                    } else {
                      setStep(1);
                    }
                  }
                }}
                className={`
                  px-4 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer
                  ${
                    draftTrack
                      ? 'bg-accent text-white hover:bg-accent-hover'
                      : 'bg-bg-surface text-text-muted cursor-not-allowed border border-border-subtle'
                  }
                `}
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Configure Side A (Baseline) */}
      {step === 1 && (
        <div className="max-w-3xl mx-auto flex flex-col gap-4">
          <div className="card p-3.5 flex items-center gap-3 bg-accent/5 border border-accent/20">
            <Info className="w-4 h-4 text-accent shrink-0" />
            <p className="text-xs text-text-secondary leading-relaxed">
              {t('aggregationdiff.selectHintA')}
            </p>
          </div>

          {/* Side A Card (Red) */}
          <div className="card p-4 border-2 border-red-500/70 bg-red-500/5 shadow-lg shadow-red-500/10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 shadow-sm shadow-red-500" />
                <span className="text-xs font-bold uppercase tracking-wider text-red-300">
                  {t('aggregationdiff.groupA')} · {t('aggregationdiff.baseline')}
                </span>
              </div>
              <span className="text-[11px] font-mono px-2.5 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30">
                {modeA === 'lap'
                  ? lapA
                    ? lapA.lapTimeMs
                      ? `Lap ${lapA.lapNumber} · ${formatTime(lapA.lapTimeMs)}`
                      : `Lap ${lapA.lapNumber}`
                    : t('aggregationdiff.selectLap')
                  : t('analysis.history.sessionsCount', {
                      n: selectedIdsA.size,
                    })}
              </span>
            </div>

            {/* Mode Toggle: Single Lap vs Session Group */}
            <div className="grid grid-cols-2 gap-1 p-0.5 rounded-md bg-bg-surface border border-border-subtle mb-3">
              <button
                type="button"
                onClick={() => updateWizardState({ modeA: 'lap' })}
                className={`py-1.5 rounded text-xs font-semibold transition-colors cursor-pointer ${
                  modeA === 'lap'
                    ? 'bg-red-500/20 text-red-200 border border-red-500/40'
                    : 'text-text-muted hover:text-text-primary'
                }`}
              >
                {t('aggregationdiff.modeSingleLap')}
              </button>
              <button
                type="button"
                onClick={() => updateWizardState({ modeA: 'sessions' })}
                className={`py-1.5 rounded text-xs font-semibold transition-colors cursor-pointer ${
                  modeA === 'sessions'
                    ? 'bg-red-500/20 text-red-200 border border-red-500/40'
                    : 'text-text-muted hover:text-text-primary'
                }`}
              >
                {t('aggregationdiff.modeSessions')}
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={labelA}
                onChange={e => updateWizardState({ labelA: e.target.value })}
                placeholder={
                  modeA === 'lap' && lapA
                    ? lapA.sessionDate
                      ? `Lap ${lapA.lapNumber} (${lapA.sessionDate})`
                      : `Lap ${lapA.lapNumber}`
                    : t('aggregationdiff.groupLabelPlaceholder')
                }
                className="flex-1 px-2.5 py-1.5 rounded-md bg-bg-surface border border-border-subtle text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-red-500"
              />

              {modeA === 'sessions' && savedAggregations.length > 0 && (
                <select
                  value=""
                  onChange={e => {
                    const rec = savedAggregations.find(
                      a => a.id === e.target.value
                    );
                    if (rec?.aggregation?.sessions?.sessionIds) {
                      updateWizardState({
                        modeA: 'sessions',
                        selectedIdsA: rec.aggregation.sessions.sessionIds,
                        labelA: rec.name || labelA,
                      });
                    }
                  }}
                  className="sm:w-64 px-2.5 py-1.5 rounded-md bg-bg-raised border border-border-subtle text-xs text-text-secondary focus:outline-none cursor-pointer"
                >
                  <option value="">
                    {t('aggregationdiff.loadSavedAggregation')}
                  </option>
                  {savedAggregations.map(rec => (
                    <option key={rec.id} value={rec.id}>
                      {rec.name} (
                      {t('analysis.history.sessionsCount', {
                        n: rec.aggregation?.sessions?.sessionIds?.length ?? 0,
                      })}
                      )
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Selector for Side A */}
          {modeA === 'lap' ? (
            <LapSelector
              sessions={sessionsWithLaps}
              expandedSessions={expandedSessions}
              onToggleSession={toggleSessionExpand}
              activeSide="A"
              lapA={lapA}
              lapB={modeB === 'lap' ? lapB : null}
              labelA={labelA.trim() || t('aggregationdiff.groupA')}
              labelB={labelB.trim() || t('aggregationdiff.groupB')}
              onSelectSideLap={handleSelectSideLap}
              driverFilter={driverFilterA}
              onDriverFilterChange={f =>
                updateWizardState({ driverFilterA: f })
              }
              weatherFilter={weatherFilterA}
              onWeatherFilterChange={f =>
                updateWizardState({ weatherFilterA: f })
              }
              uniqueDrivers={uniqueDrivers}
            />
          ) : (
            <SessionSelector
              sessions={sessions}
              selectedIds={selectedIdsA}
              onSelectionChange={setSelectedIdsA}
              driverFilter={driverFilterA}
              onDriverFilterChange={f => updateWizardState({ driverFilterA: f })}
              weatherFilter={weatherFilterA}
              onWeatherFilterChange={f =>
                updateWizardState({ weatherFilterA: f })
              }
              uniqueDrivers={uniqueDrivers}
            />
          )}

          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setStep(0)}
              className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors cursor-pointer"
            >
              <ArrowLeft size={14} />
              {t('common.back')}
            </button>

            <button
              disabled={!isSideAReady}
              onClick={() => setStep(2)}
              className={`
                flex items-center gap-1.5 px-5 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer
                ${
                  isSideAReady
                    ? 'bg-accent text-white hover:bg-accent-hover'
                    : 'bg-bg-surface text-text-muted cursor-not-allowed border border-border-subtle'
                }
              `}
            >
              <span>{t('common.next')}</span>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Configure Side B (Comparison) */}
      {step === 2 && (
        <div className="max-w-3xl mx-auto flex flex-col gap-4">
          <div className="card p-3.5 flex items-center gap-3 bg-accent/5 border border-accent/20">
            <Info className="w-4 h-4 text-accent shrink-0" />
            <p className="text-xs text-text-secondary leading-relaxed">
              {t('aggregationdiff.selectHintB')}
            </p>
          </div>

          {/* Compact summary bar of Side A (Baseline) */}
          <div
            onClick={() => setStep(1)}
            className="card px-3.5 py-2.5 flex items-center justify-between border border-red-500/30 bg-red-500/5 hover:border-red-500/60 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-red-300 shrink-0">
                {t('aggregationdiff.groupA')} · {t('aggregationdiff.baseline')}
              </span>
              {labelA.trim() && (
                <span className="text-xs text-text-secondary truncate">
                  — {labelA.trim()}
                </span>
              )}
            </div>
            <span className="text-[11px] font-mono px-2.5 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30 shrink-0">
              {modeA === 'lap'
                ? lapA
                  ? lapA.lapTimeMs
                    ? `Lap ${lapA.lapNumber} · ${formatTime(lapA.lapTimeMs)}`
                    : `Lap ${lapA.lapNumber}`
                  : t('aggregationdiff.selectLap')
                : t('analysis.history.sessionsCount', {
                    n: selectedIdsA.size,
                  })}
            </span>
          </div>

          {/* Side B Card (Blue) */}
          <div className="card p-4 border-2 border-blue-500/70 bg-blue-500/5 shadow-lg shadow-blue-500/10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-500 shadow-sm shadow-blue-500" />
                <span className="text-xs font-bold uppercase tracking-wider text-blue-300">
                  {t('aggregationdiff.groupB')} · {t('aggregationdiff.comparison')}
                </span>
              </div>
              <span className="text-[11px] font-mono px-2.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30">
                {modeB === 'lap'
                  ? lapB
                    ? lapB.lapTimeMs
                      ? `Lap ${lapB.lapNumber} · ${formatTime(lapB.lapTimeMs)}`
                      : `Lap ${lapB.lapNumber}`
                    : t('aggregationdiff.selectLap')
                  : t('analysis.history.sessionsCount', {
                      n: selectedIdsB.size,
                    })}
              </span>
            </div>

            {/* Mode Toggle: Single Lap vs Session Group */}
            <div className="grid grid-cols-2 gap-1 p-0.5 rounded-md bg-bg-surface border border-border-subtle mb-3">
              <button
                type="button"
                onClick={() => updateWizardState({ modeB: 'lap' })}
                className={`py-1.5 rounded text-xs font-semibold transition-colors cursor-pointer ${
                  modeB === 'lap'
                    ? 'bg-blue-500/20 text-blue-200 border border-blue-500/40'
                    : 'text-text-muted hover:text-text-primary'
                }`}
              >
                {t('aggregationdiff.modeSingleLap')}
              </button>
              <button
                type="button"
                onClick={() => updateWizardState({ modeB: 'sessions' })}
                className={`py-1.5 rounded text-xs font-semibold transition-colors cursor-pointer ${
                  modeB === 'sessions'
                    ? 'bg-blue-500/20 text-blue-200 border border-blue-500/40'
                    : 'text-text-muted hover:text-text-primary'
                }`}
              >
                {t('aggregationdiff.modeSessions')}
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={labelB}
                onChange={e => updateWizardState({ labelB: e.target.value })}
                placeholder={
                  modeB === 'lap' && lapB
                    ? lapB.sessionDate
                      ? `Lap ${lapB.lapNumber} (${lapB.sessionDate})`
                      : `Lap ${lapB.lapNumber}`
                    : t('aggregationdiff.groupLabelPlaceholder')
                }
                className="flex-1 px-2.5 py-1.5 rounded-md bg-bg-surface border border-border-subtle text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-blue-500"
              />

              {modeB === 'sessions' && savedAggregations.length > 0 && (
                <select
                  value=""
                  onChange={e => {
                    const rec = savedAggregations.find(
                      a => a.id === e.target.value
                    );
                    if (rec?.aggregation?.sessions?.sessionIds) {
                      updateWizardState({
                        modeB: 'sessions',
                        selectedIdsB: rec.aggregation.sessions.sessionIds,
                        labelB: rec.name || labelB,
                      });
                    }
                  }}
                  className="sm:w-64 px-2.5 py-1.5 rounded-md bg-bg-raised border border-border-subtle text-xs text-text-secondary focus:outline-none cursor-pointer"
                >
                  <option value="">
                    {t('aggregationdiff.loadSavedAggregation')}
                  </option>
                  {savedAggregations.map(rec => (
                    <option key={rec.id} value={rec.id}>
                      {rec.name} (
                      {t('analysis.history.sessionsCount', {
                        n: rec.aggregation?.sessions?.sessionIds?.length ?? 0,
                      })}
                      )
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Selector for Side B */}
          {modeB === 'lap' ? (
            <LapSelector
              sessions={sessionsWithLaps}
              expandedSessions={expandedSessions}
              onToggleSession={toggleSessionExpand}
              activeSide="B"
              lapA={modeA === 'lap' ? lapA : null}
              lapB={lapB}
              labelA={labelA.trim() || t('aggregationdiff.groupA')}
              labelB={labelB.trim() || t('aggregationdiff.groupB')}
              onSelectSideLap={handleSelectSideLap}
              driverFilter={driverFilterB}
              onDriverFilterChange={f =>
                updateWizardState({ driverFilterB: f })
              }
              weatherFilter={weatherFilterB}
              onWeatherFilterChange={f =>
                updateWizardState({ weatherFilterB: f })
              }
              uniqueDrivers={uniqueDrivers}
            />
          ) : (
            <SessionSelector
              sessions={sessions}
              selectedIds={selectedIdsB}
              onSelectionChange={setSelectedIdsB}
              driverFilter={driverFilterB}
              onDriverFilterChange={f => updateWizardState({ driverFilterB: f })}
              weatherFilter={weatherFilterB}
              onWeatherFilterChange={f =>
                updateWizardState({ weatherFilterB: f })
              }
              uniqueDrivers={uniqueDrivers}
            />
          )}

          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setStep(1)}
              className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors cursor-pointer"
            >
              <ArrowLeft size={14} />
              {t('common.back')}
            </button>

            <button
              disabled={analyzing || !canCompare}
              onClick={() => handleAnalyze()}
              className={`
                flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer
                ${
                  canCompare && !analyzing
                    ? 'bg-accent text-white hover:bg-accent-hover'
                    : 'bg-bg-surface text-text-muted cursor-not-allowed border border-border-subtle'
                }
              `}
            >
              {analyzing ? (
                <LoadingSpinner size="sm" />
              ) : (
                <GitCompareArrows size={14} />
              )}
              <span>
                {analyzing
                  ? t('gearshifting.analyzingLap')
                  : t('aggregationdiff.compare')}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && (
        <div className="flex flex-col gap-6">
          {analyzing ? (
            <LoadingSpinner />
          ) : !analysisResult ||
            (lapsA.length === 0 && lapsB.length === 0) ? (
            <div className="card p-12 flex flex-col items-center justify-center gap-3 text-center">
              <span className="text-3xl opacity-60">📊</span>
              <p className="text-sm text-text-secondary">
                {t('aggregation.noData')}
              </p>
              <p className="text-xs text-text-muted">
                {t('aggregation.noDataHint')}
              </p>
            </div>
          ) : (
            <>
              {/* Shared Top Summary Banner (Side A = Red, Side B = Blue) */}
              <ComparisonSummaryBanner
                titleA={
                  modeA === 'lap' && lapA && !labelA.trim()
                    ? `${lapA.sessionDate}${lapA.sessionTime ? ' ' + lapA.sessionTime : ''}` || groupALabel
                    : groupALabel
                }
                badgeA={
                  modeA === 'lap' && lapA
                    ? `Lap ${lapA.lapNumber}`
                    : t('aggregationdiff.baseline')
                }
                subtitleA={
                  modeA === 'lap'
                    ? t('aggregationdiff.baseline')
                    : t('aggregation.lapsIncluded', {
                        count: summaryA?.lapCount ?? lapsA.length,
                      })
                }
                timeLabelA={
                  modeA === 'lap'
                    ? t('lapdiff.lapTime')
                    : t('aggregationdiff.bestLap')
                }
                timeMsA={summaryA?.bestLapMs || lapA?.lapTimeMs || 0}
                titleB={
                  modeB === 'lap' && lapB && !labelB.trim()
                    ? `${lapB.sessionDate}${lapB.sessionTime ? ' ' + lapB.sessionTime : ''}` || groupBLabel
                    : groupBLabel
                }
                badgeB={
                  modeB === 'lap' && lapB
                    ? `Lap ${lapB.lapNumber}`
                    : t('aggregationdiff.comparison')
                }
                subtitleB={
                  modeB === 'lap'
                    ? t('aggregationdiff.comparison')
                    : t('aggregation.lapsIncluded', {
                        count: summaryB?.lapCount ?? lapsB.length,
                      })
                }
                timeLabelB={
                  modeB === 'lap'
                    ? t('lapdiff.lapTime')
                    : t('aggregationdiff.bestLap')
                }
                timeMsB={summaryB?.bestLapMs || lapB?.lapTimeMs || 0}
              />

              {/* Missing track shape notice */}
              {!hasShape && (
                <div className="card p-6 flex flex-col items-center justify-center gap-3 text-center border-amber-500/30 bg-amber-500/5 max-w-lg mx-auto">
                  <AlertTriangle className="w-8 h-8 text-amber-500" />
                  <h4 className="text-sm font-semibold text-text-primary">
                    {t('aggregation.trackShapeRequired')}
                  </h4>
                  <p className="text-xs text-text-muted max-w-md">
                    {t('aggregation.trackShapeRequiredHint')}
                  </p>
                </div>
              )}

              {/* Interactive Diff Map + Key Differences Side-by-Side / Stacked Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                {/* Left / Main Column: Interactive Track Map + Metric Diff Chart */}
                <div className="lg:col-span-7 flex flex-col items-center gap-3">
                  {refTrack.length > 0 && (
                    <>
                      <div className="w-full flex items-center justify-between px-1 min-h-[20px]">
                        {!anchors ? (
                          <span className="text-[11px] text-text-muted">
                            {t('lapdiff.clickToPlace')}
                          </span>
                        ) : (
                          <span className="text-[11px] text-purple-300 font-mono font-medium">
                            {t('lapdiff.segmentRange', {
                              start: String(segmentComparison?.startPct ?? 0),
                              end: String(segmentComparison?.endPct ?? 100),
                            })}{' '}
                            ({segmentComparison?.startDistM ?? 0}m –{' '}
                            {segmentComparison?.endDistM ?? 0}m)
                          </span>
                        )}
                        {anchors && (
                          <button
                            type="button"
                            onClick={() => setAnchors(null)}
                            className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium text-text-muted bg-bg-surface border border-border-subtle hover:text-text-primary transition-colors cursor-pointer"
                          >
                            <X size={10} />
                            {t('lapdiff.clearAnchors')}
                          </button>
                        )}
                      </div>

                      <div className="relative aspect-square w-full rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
                        <InteractiveTrackMap
                          anchor={mapAnchor}
                          trackShape={trackShape}
                          trackAnnotations={trackAnnotations}
                          points={refTrack.map(s => ({
                            lat: s.latitudeRaw / 1e6,
                            lng: s.longitudeRaw / 1e6,
                          }))}
                          focusBounds={focusedZoneBounds}
                          renderOverlay={renderOverlay}
                          hitTestInteractive={hitTestInteractive}
                          onDragHandle={onDragHandle}
                          onClick={handleTrackClick}
                          onHover={handleHover}
                          drawRibbonFill={false}
                          cursor={
                            !anchors && (hoveredItem || hoveredProgress !== null)
                              ? 'crosshair'
                              : undefined
                          }
                          className="w-full h-full"
                        >
                          {/* Top-Right Burger Menu for Layer Toggles */}
                          <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5 z-10">
                            <button
                              type="button"
                              onClick={() => setLayersMenuOpen(prev => !prev)}
                              aria-label={t('aggregationdiff.toggleLayers')}
                              aria-expanded={layersMenuOpen}
                              className={`backdrop-blur-md border p-2 rounded-full transition-colors shadow-lg cursor-pointer flex items-center justify-center ${
                                layersMenuOpen
                                  ? 'bg-bg-surface/95 border-white/25 text-white hover:bg-bg-hover'
                                  : 'bg-bg-base/85 border-white/15 text-text-secondary hover:text-text-primary hover:bg-bg-surface'
                              }`}
                            >
                              {layersMenuOpen ? (
                                <X className="h-4 w-4" />
                              ) : (
                                <Menu className="h-4 w-4" />
                              )}
                            </button>

                            {layersMenuOpen && (
                              <div className="flex flex-col items-end gap-1.5">
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateWizardState({
                                      showDiffHeatmap: !showDiffHeatmap,
                                    })
                                  }
                                  className={`backdrop-blur-md border text-[10px] font-mono px-2.5 py-1 rounded-full transition-colors shadow-lg cursor-pointer flex items-center gap-1.5 ${
                                    showDiffHeatmap
                                      ? 'bg-red-500/20 border-red-400/50 text-red-200 hover:bg-red-500/30'
                                      : 'bg-bg-base/85 border-white/10 text-text-secondary hover:text-text-primary hover:bg-bg-surface'
                                  }`}
                                >
                                  <Flame
                                    className={`h-3 w-3 ${
                                      showDiffHeatmap
                                        ? 'text-red-400'
                                        : 'text-text-muted'
                                    }`}
                                  />
                                  <span>
                                    {t('aggregationdiff.layerDiffHeatmap')}
                                  </span>
                                </button>

                                <button
                                  type="button"
                                  onClick={() =>
                                    updateWizardState({
                                      showFasterSegments: !showFasterSegments,
                                    })
                                  }
                                  className={`backdrop-blur-md border text-[10px] font-mono px-2.5 py-1 rounded-full transition-colors shadow-lg cursor-pointer flex items-center gap-1.5 ${
                                    showFasterSegments
                                      ? 'bg-emerald-500/20 border-emerald-400/50 text-emerald-200 hover:bg-emerald-500/30'
                                      : 'bg-bg-base/85 border-white/10 text-text-secondary hover:text-text-primary hover:bg-bg-surface'
                                  }`}
                                >
                                  <Gauge
                                    className={`h-3 w-3 ${
                                      showFasterSegments
                                        ? 'text-emerald-400'
                                        : 'text-text-muted'
                                    }`}
                                  />
                                  <span>
                                    {t('aggregationdiff.layerFasterSegments')}
                                  </span>
                                </button>

                                <button
                                  type="button"
                                  onClick={() =>
                                    updateWizardState({
                                      showEventDiffs: !showEventDiffs,
                                    })
                                  }
                                  className={`backdrop-blur-md border text-[10px] font-mono px-2.5 py-1 rounded-full transition-colors shadow-lg cursor-pointer flex items-center gap-1.5 ${
                                    showEventDiffs
                                      ? 'bg-accent/25 border-accent/50 text-text-primary hover:bg-accent/35'
                                      : 'bg-bg-base/85 border-white/10 text-text-secondary hover:text-text-primary hover:bg-bg-surface'
                                  }`}
                                >
                                  <Layers
                                    className={`h-3 w-3 ${
                                      showEventDiffs
                                        ? 'text-accent'
                                        : 'text-text-muted'
                                    }`}
                                  />
                                  <span>
                                    {t('aggregationdiff.layerEventDiffs')}
                                  </span>
                                </button>

                                <button
                                  type="button"
                                  onClick={() =>
                                    updateWizardState({
                                      dimSmallDiffs: !dimSmallDiffs,
                                    })
                                  }
                                  className={`backdrop-blur-md border text-[10px] font-mono px-2.5 py-1 rounded-full transition-colors shadow-lg cursor-pointer flex items-center gap-1.5 ${
                                    dimSmallDiffs
                                      ? 'bg-amber-500/20 border-amber-400/50 text-amber-200 hover:bg-amber-500/30'
                                      : 'bg-bg-base/85 border-white/10 text-text-secondary hover:text-text-primary hover:bg-bg-surface'
                                  }`}
                                >
                                  <Filter
                                    className={`h-3 w-3 ${
                                      dimSmallDiffs
                                        ? 'text-amber-400'
                                        : 'text-text-muted'
                                    }`}
                                  />
                                  <span>
                                    {t('aggregationdiff.hideUnchanged')}
                                  </span>
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Clear Focused Diff Pill */}
                          {focusedDiffKey && (
                            <div className="absolute bottom-3 left-3 z-10">
                              <button
                                type="button"
                                onClick={() =>
                                  updateWizardState({ focusedDiffKey: '' })
                                }
                                className="backdrop-blur-md bg-amber-500/25 border border-amber-400/60 text-amber-200 text-[11px] font-medium px-3 py-1 rounded-full shadow-lg flex items-center gap-1.5 cursor-pointer hover:bg-amber-500/35"
                              >
                                <X size={12} />
                                <span>{t('aggregationdiff.clearFocus')}</span>
                              </button>
                            </div>
                          )}

                          {/* Hover Tooltip */}
                          {hoveredItem && (
                            <div
                              className="absolute pointer-events-none z-[600] bg-bg-surface/95 backdrop-blur-sm border border-border-subtle rounded-lg p-2.5 shadow-xl text-xs flex flex-col gap-1 -translate-x-1/2 -translate-y-full -mt-3 min-w-[225px]"
                              style={{
                                left: hoveredItem.x,
                                top: hoveredItem.y,
                              }}
                            >
                              {hoveredItem.item?.lineZone ? (
                                <>
                                  <div className="flex items-center justify-between gap-2 font-semibold text-purple-300">
                                    <span>
                                      {t('aggregationdiff.filterLine')}
                                    </span>
                                    {(hoveredItem.item.cornerName ||
                                      hoveredItem.cornerName) && (
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-bg-hover text-text-secondary">
                                        {hoveredItem.item.cornerName ||
                                          hoveredItem.cornerName}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[11px] text-text-primary font-mono">
                                    {t('aggregationdiff.lineSeparation', {
                                      dist: hoveredItem.item.lineZone.maxLateralDiffM.toFixed(
                                        1
                                      ),
                                      len: Math.round(
                                        hoveredItem.item.lineZone.endDistanceM -
                                          hoveredItem.item.lineZone
                                            .startDistanceM
                                      ),
                                    })}
                                  </div>
                                </>
                              ) : hoveredItem.item?.segmentDiff ? (
                                <>
                                  <div className="flex items-center justify-between gap-2 font-semibold">
                                    <span className="text-text-primary">
                                      {hoveredItem.item.category === 'braking'
                                        ? t('aggregation.brakingSegment')
                                        : hoveredItem.item.category ===
                                          'acceleration'
                                        ? t('aggregation.accelerationSegment')
                                        : t('aggregation.upshiftSegment')}
                                    </span>
                                    {(hoveredItem.item.cornerName ||
                                      hoveredItem.cornerName) && (
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-bg-hover text-text-secondary">
                                        {hoveredItem.item.cornerName ||
                                          hoveredItem.cornerName}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[11px] font-semibold text-amber-300">
                                    {formatSignedDistanceHeadline(
                                      hoveredItem.item.segmentDiff
                                    )}
                                  </div>
                                  {hoveredItem.item.segmentDiff.segmentA && (
                                    <div className="text-[11px] flex justify-between gap-3 text-red-300 font-mono">
                                      <span>{groupALabel}:</span>
                                      <span>
                                        {getSegDistances(
                                          hoveredItem.item.segmentDiff.segmentA,
                                          effectiveTotalLengthM
                                        ).medDistM.toFixed(1)}
                                        m
                                      </span>
                                    </div>
                                  )}
                                  {hoveredItem.item.segmentDiff.segmentB && (
                                    <div className="text-[11px] flex justify-between gap-3 text-blue-300 font-mono">
                                      <span>{groupBLabel}:</span>
                                      <span>
                                        {getSegDistances(
                                          hoveredItem.item.segmentDiff.segmentB,
                                          effectiveTotalLengthM
                                        ).medDistM.toFixed(1)}
                                        m
                                      </span>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="flex items-center justify-between gap-2 font-semibold text-text-primary">
                                  <span>{t('aggregationdiff.filterLine')}</span>
                                  <div className="flex items-center gap-1.5">
                                    {hoveredItem.cornerName && (
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-bg-hover text-text-secondary">
                                        {hoveredItem.cornerName}
                                      </span>
                                    )}
                                    {hoveredItem.distanceM !== undefined && (
                                      <span className="text-[10px] font-mono text-text-muted">
                                        @{Math.round(hoveredItem.distanceM)}m
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Average Speed & Speed Diff at hovered point */}
                              {(hoveredItem.speedAKmh ||
                                hoveredItem.speedBKmh) && (
                                <div
                                  className={`flex flex-col gap-0.5 ${
                                    hoveredItem.item
                                      ? 'pt-1.5 mt-1 border-t border-border-subtle'
                                      : 'pt-0.5'
                                  }`}
                                >
                                  {hoveredItem.speedAKmh !== undefined &&
                                    hoveredItem.speedAKmh > 0 && (
                                      <div className="text-[11px] flex items-center justify-between gap-3 text-red-300 font-mono">
                                        <span className="flex items-center gap-1.5">
                                          <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" />
                                          {groupALabel}{' '}
                                          {t('aggregationdiff.avgSpeed')}:
                                        </span>
                                        <span className="font-semibold">
                                          {hoveredItem.speedAKmh.toFixed(1)}{' '}
                                          km/h
                                        </span>
                                      </div>
                                    )}
                                  {hoveredItem.speedBKmh !== undefined &&
                                    hoveredItem.speedBKmh > 0 && (
                                      <div className="text-[11px] flex items-center justify-between gap-3 text-blue-300 font-mono">
                                        <span className="flex items-center gap-1.5">
                                          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" />
                                          {groupBLabel}{' '}
                                          {t('aggregationdiff.avgSpeed')}:
                                        </span>
                                        <span className="font-semibold">
                                          {hoveredItem.speedBKmh.toFixed(1)}{' '}
                                          km/h
                                        </span>
                                      </div>
                                    )}
                                  {hoveredItem.speedDiffKmh !== undefined && (
                                    <div className="text-[11px] flex items-center justify-between gap-3 font-mono pt-0.5">
                                      <span className="text-text-secondary">
                                        {t('aggregationdiff.speedDiff')}:
                                      </span>
                                      <span
                                        className={`font-bold ${
                                          hoveredItem.speedDiffKmh > 0.15
                                            ? 'text-blue-300'
                                            : hoveredItem.speedDiffKmh < -0.15
                                            ? 'text-red-300'
                                            : 'text-text-secondary'
                                        }`}
                                      >
                                        {hoveredItem.speedDiffKmh > 0.15
                                          ? `${groupBLabel} +${hoveredItem.speedDiffKmh.toFixed(1)} km/h`
                                          : hoveredItem.speedDiffKmh < -0.15
                                          ? `${groupALabel} +${Math.abs(hoveredItem.speedDiffKmh).toFixed(1)} km/h`
                                          : '±0.0 km/h'}
                                      </span>
                                    </div>
                                  )}
                                  {!hoveredItem.item &&
                                    hoveredItem.lateralDiffM !== undefined && (
                                      <div className="text-[10px] flex items-center justify-between gap-3 font-mono text-text-muted pt-0.5">
                                        <span>
                                          {t('aggregationdiff.lineOffset')}:
                                        </span>
                                        <span>
                                          {hoveredItem.lateralDiffM.toFixed(2)}m
                                        </span>
                                      </div>
                                    )}
                                </div>
                              )}
                            </div>
                          )}
                        </InteractiveTrackMap>
                      </div>

                      {/* Map Legend */}
                      <div className="flex items-center justify-center gap-4 sm:gap-5 py-2 px-4 rounded-full bg-bg-raised/80 backdrop-blur-md border border-border-subtle/80 shadow-lg text-xs flex-wrap">
                        <div className="flex items-center gap-1.5">
                          <span className="w-3 h-2 rounded-xs bg-red-500 inline-block" />
                          <span className="text-[10px] text-red-300 font-semibold uppercase tracking-wider">
                            {groupALabel} ({t('aggregationdiff.leftInner')})
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-3 h-2 rounded-xs bg-blue-500 inline-block" />
                          <span className="text-[10px] text-blue-300 font-semibold uppercase tracking-wider">
                            {groupBLabel} ({t('aggregationdiff.rightOuter')})
                          </span>
                        </div>
                        {showFasterSegments && (
                          <div className="flex items-center gap-1.5">
                            <span className="w-5 h-2 rounded-full bg-gradient-to-r from-red-500 via-slate-600 to-blue-500 inline-block border border-white/10" />
                            <span className="text-[10px] text-text-secondary uppercase tracking-wider">
                              {t('aggregationdiff.fasterSegmentLegend')}
                            </span>
                          </div>
                        )}
                        {showDiffHeatmap && (
                          <div className="flex items-center gap-1.5">
                            <span className="w-3 h-1.5 rounded-full bg-slate-400/50 inline-block" />
                            <span className="text-[10px] text-text-muted uppercase tracking-wider">
                              {t('aggregationdiff.sharedLine')}
                            </span>
                          </div>
                        )}
                        {showEventDiffs && (
                          <>
                            <div className="flex items-center gap-1.5">
                              <span className="w-2.5 h-2.5 rounded-xs bg-[#ef4444]" />
                              <span className="text-[10px] text-text-muted uppercase tracking-wider">
                                {t('aggregation.brakingPoints')}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="w-2.5 h-2.5 rounded-xs bg-[#22c55e]" />
                              <span className="text-[10px] text-text-muted uppercase tracking-wider">
                                {t('aggregation.accelerationPoints')}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="w-2.5 h-2.5 rounded-xs bg-[#38bdf8]" />
                              <span className="text-[10px] text-text-muted uppercase tracking-wider">
                                {t('aggregation.upshiftPoints')}
                              </span>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Shared Synchronized Metric & Time Delta Chart */}
                      {timeSeries.length > 0 && (
                        <LapDiffTimeChart
                          timeSeries={timeSeries}
                          metric={metric}
                          viewLap={viewLap}
                          hoveredProgress={hoveredProgress}
                          onHoverProgress={updateHoverAtProgress}
                          anchors={anchors}
                          onChangeAnchors={setAnchors}
                          onSelectProgress={placeAnchorsAtProgress}
                          trackAnnotations={trackAnnotations}
                          labelA={groupALabel}
                          labelB={groupBLabel}
                        />
                      )}
                    </>
                  )}
                </div>

                {/* Right Column: Shared DiffLegend + Ranked Key Differences Panel */}
                <div className="lg:col-span-5 flex flex-col gap-4">
                  <DiffLegend
                    lapA={{
                      sessionDate: groupALabel,
                      sessionTime: '',
                      lapNumber: summaryA?.lapCount ?? lapsA.length,
                      lapTimeMs: fullLapAMs,
                      color: LAP_COLORS[0],
                    }}
                    lapB={{
                      sessionDate: groupBLabel,
                      sessionTime: '',
                      lapNumber: summaryB?.lapCount ?? lapsB.length,
                      lapTimeMs: fullLapBMs,
                      color: LAP_COLORS[1],
                    }}
                    metric={metric}
                    onMetricChange={setMetric}
                    viewLap={viewLap}
                    onViewLapChange={setViewLap}
                    stats={stats}
                    hoverInfo={hoverInfo}
                    timeDiff={timeDiff}
                    hasAnchors={anchors !== null}
                    onClearAnchors={() => setAnchors(null)}
                    labelA={groupALabel}
                    labelB={groupBLabel}
                    showLapCards={false}
                  />

                  <div className="card overflow-hidden flex flex-col">
                    <div className="px-4 py-3.5 border-b border-border-subtle">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xs font-bold uppercase tracking-widest text-text-primary flex items-center gap-2">
                          <Target size={14} className="text-accent" />
                          <span>{t('aggregationdiff.keyDifferencesTitle')}</span>
                        </h3>
                        <span className="text-[11px] font-mono text-text-muted">
                          {filteredRangeClusters.length}
                        </span>
                      </div>
                      <p className="text-[11px] text-text-muted mt-0.5">
                        {t('aggregationdiff.keyDifferencesSubtitle')}
                      </p>

                      {/* Category Filter Pills */}
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {(
                          [
                            { id: 'all', label: t('aggregationdiff.filterAll') },
                            {
                              id: 'line',
                              label: t('aggregationdiff.filterLine'),
                            },
                            {
                              id: 'braking',
                              label: t('aggregationdiff.filterBraking'),
                            },
                            {
                              id: 'acceleration',
                              label: t('aggregationdiff.filterAcceleration'),
                            },
                            {
                              id: 'upshift',
                              label: t('aggregationdiff.filterUpshift'),
                            },
                            {
                              id: 'topspeed',
                              label: t('aggregationdiff.filterTopSpeed'),
                            },
                          ] as Array<{ id: DiffFilterCategory; label: string }>
                        ).map(tab => {
                          const active = diffFilter === tab.id;
                          return (
                            <button
                              key={tab.id}
                              type="button"
                              onClick={() =>
                                updateWizardState({ diffFilter: tab.id })
                              }
                              className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors cursor-pointer flex items-center gap-1.5 ${
                                active
                                  ? 'bg-accent text-white'
                                  : 'bg-bg-surface text-text-secondary hover:text-text-primary border border-border-subtle'
                              }`}
                            >
                              <span>{tab.label}</span>
                              <span
                                className={`text-[10px] font-mono px-1 rounded ${
                                  active
                                    ? 'bg-white/20 text-white'
                                    : 'bg-bg-hover text-text-muted'
                                }`}
                              >
                                {categoryCounts[tab.id]}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="max-h-[540px] overflow-y-auto divide-y divide-white/10">
                      {filteredRangeClusters.length === 0 ? (
                        <div className="p-8 text-center text-xs text-text-muted">
                          {t('aggregationdiff.noSignificantDiffs')}
                        </div>
                      ) : (
                        filteredRangeClusters.map(cluster => {
                          const isFocused = focusedDiffKey === cluster.key;
                          const estMs = cluster.timeDiffMs;
                          const isGroupAFaster =
                            Number.isFinite(estMs) && estMs > 0;
                          const isGroupBFaster =
                            Number.isFinite(estMs) && estMs < 0;

                          const entryBgClass = isGroupAFaster
                            ? isFocused
                              ? 'bg-red-500/30 border-l-4 border-l-amber-400 ring-1 ring-inset ring-amber-400/60'
                              : 'bg-red-500/15 hover:bg-red-500/25 border-l-4 border-l-red-500'
                            : isGroupBFaster
                            ? isFocused
                              ? 'bg-blue-500/30 border-l-4 border-l-amber-400 ring-1 ring-inset ring-amber-400/60'
                              : 'bg-blue-500/15 hover:bg-blue-500/25 border-l-4 border-l-blue-500'
                            : isFocused
                            ? 'bg-amber-500/15 border-l-4 border-l-amber-400'
                            : 'hover:bg-bg-hover border-l-4 border-l-transparent';

                          const renderEstTimeBadge = () => {
                            if (!Number.isFinite(estMs)) {
                              return null;
                            }
                            const absSec =
                              (Math.abs(estMs) / 1000).toFixed(2) + 's';
                            if (estMs < -5) {
                              return (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-slate-950/65 text-blue-200 border border-blue-400/45 shadow-xs">
                                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                                  {t('aggregationdiff.estTimeGained', {
                                    time: absSec,
                                    group: groupBLabel,
                                  })}
                                </span>
                              );
                            }
                            if (estMs > 5) {
                              return (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-slate-950/65 text-red-200 border border-red-400/45 shadow-xs">
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                                  {t('aggregationdiff.estTimeLost', {
                                    time: absSec,
                                    group: groupALabel,
                                  })}
                                </span>
                              );
                            }
                            return (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono text-white/80 bg-slate-950/55 border border-white/15">
                                {t('aggregationdiff.estTimeNeutral')}
                              </span>
                            );
                          };

                          const rangeStartM = Math.round(cluster.startDistanceM);
                          const rangeEndM = Math.max(
                            rangeStartM + 1,
                            Math.round(cluster.endDistanceM)
                          );
                          const avgSpdDiff =
                            cluster.avgSpeedAKmh > 2 && cluster.avgSpeedBKmh > 2
                              ? cluster.avgSpeedBKmh - cluster.avgSpeedAKmh
                              : undefined;

                          return (
                            <button
                              key={cluster.key}
                              type="button"
                              onClick={() =>
                                updateWizardState({
                                  focusedDiffKey: isFocused ? '' : cluster.key,
                                })
                              }
                              className={`w-full p-3.5 text-left transition-colors cursor-pointer flex flex-col gap-2 ${entryBgClass}`}
                            >
                              {/* Range Header Row: Distance Span + Corner Name + Net Time Difference */}
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono font-bold bg-slate-950/70 text-white border border-white/20 shadow-xs">
                                    {rangeStartM}m – {rangeEndM}m
                                  </span>
                                  {cluster.cornerName && (
                                    <span className="text-xs font-bold text-white">
                                      {cluster.cornerName}
                                    </span>
                                  )}
                                  <span className="text-[10px] font-mono text-white/60">
                                    ({rangeEndM - rangeStartM}m)
                                  </span>
                                </div>
                                {renderEstTimeBadge()}
                              </div>

                              {/* Section Average Speed Summary */}
                              {avgSpdDiff !== undefined && (
                                <div className="flex items-center justify-between text-[11px] font-mono">
                                  <span className="text-white/75">
                                    {t('aggregationdiff.avgSpeed')}:{' '}
                                    <span className="text-red-200 font-semibold">
                                      {cluster.avgSpeedAKmh.toFixed(1)}
                                    </span>{' '}
                                    vs{' '}
                                    <span className="text-blue-200 font-semibold">
                                      {cluster.avgSpeedBKmh.toFixed(1)}
                                    </span>{' '}
                                    km/h
                                  </span>
                                  <span
                                    className={`font-bold ${
                                      avgSpdDiff > 0.15
                                        ? 'text-blue-200'
                                        : avgSpdDiff < -0.15
                                        ? 'text-red-200'
                                        : 'text-white/80'
                                    }`}
                                  >
                                    {avgSpdDiff > 0.15
                                      ? `${groupBLabel} +${avgSpdDiff.toFixed(1)} km/h`
                                      : avgSpdDiff < -0.15
                                      ? `${groupALabel} +${Math.abs(avgSpdDiff).toFixed(1)} km/h`
                                      : '±0.0 km/h'}
                                  </span>
                                </div>
                              )}

                              {/* Possible Reasons within this Range */}
                              {cluster.reasons.length > 0 && (
                                <div className="flex flex-col gap-1.5 pt-1.5 border-t border-white/10">
                                  <span className="text-[10px] font-semibold uppercase tracking-wider text-white/60">
                                    {t('aggregationdiff.possibleReasons')}
                                  </span>
                                  {cluster.reasons.map((reason, rIdx) => {
                                    const isReasonFiltered =
                                      diffFilter !== 'all' &&
                                      reason.category === diffFilter;
                                    const reasonBoxClass = isReasonFiltered
                                      ? 'bg-slate-950/75 border-accent/60 ring-1 ring-inset ring-accent/40'
                                      : 'bg-slate-950/50 border-white/10';

                                    if (reason.category === 'line' && reason.lineZone) {
                                      const lz = reason.lineZone;
                                      const zoneLen = Math.max(
                                        1,
                                        Math.round(lz.endDistanceM - lz.startDistanceM)
                                      );
                                      const bFurtherLeft =
                                        (lz.signedLateralDiffM || 0) > 0;
                                      return (
                                        <div
                                          key={`reason-line-${rIdx}`}
                                          className={`rounded-md border px-2.5 py-1.5 flex flex-col gap-1 ${reasonBoxClass}`}
                                        >
                                          <div className="flex items-center justify-between gap-2 flex-wrap">
                                            <div className="flex items-center gap-1.5">
                                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-purple-500/20 text-purple-200 border border-purple-400/40">
                                                <Route
                                                  size={10}
                                                  className="text-purple-300"
                                                />
                                                {t('aggregationdiff.filterLine')}
                                              </span>
                                              <span className="text-[11px] font-semibold text-white">
                                                {t('aggregationdiff.lineSeparation', {
                                                  dist: (
                                                    lz.maxLateralDiffM || 0
                                                  ).toFixed(1),
                                                  len: zoneLen,
                                                })}
                                              </span>
                                            </div>
                                          </div>
                                          <div className="flex items-center justify-between text-[10px] font-mono">
                                            <span className="text-white/70">
                                              Ø {(lz.meanLateralDiffM || 0).toFixed(2)}m
                                            </span>
                                            <span className="text-blue-200">
                                              {groupBLabel}{' '}
                                              {Math.abs(
                                                lz.signedLateralDiffM || 0
                                              ).toFixed(1)}
                                              m{' '}
                                              {bFurtherLeft
                                                ? 'further left/inner'
                                                : 'further right/outer'}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    }

                                    if (
                                      reason.category === 'topspeed' &&
                                      reason.topSpeedDiff
                                    ) {
                                      const ts = reason.topSpeedDiff;
                                      return (
                                        <div
                                          key={`reason-topspeed-${rIdx}`}
                                          className={`rounded-md border px-2.5 py-1.5 flex items-center justify-between gap-2 flex-wrap ${reasonBoxClass}`}
                                        >
                                          <div className="flex items-center gap-1.5">
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-200 border border-amber-400/40">
                                              <Gauge
                                                size={10}
                                                className="text-amber-300"
                                              />
                                              {t('aggregationdiff.filterTopSpeed')}
                                            </span>
                                            <span
                                              className={`text-[11px] font-mono font-bold ${
                                                ts.diffKmh > 0
                                                  ? 'text-blue-200'
                                                  : 'text-red-200'
                                              }`}
                                            >
                                              {ts.diffKmh > 0
                                                ? `${groupBLabel} +${ts.diffKmh.toFixed(1)} km/h`
                                                : `${groupALabel} +${Math.abs(ts.diffKmh).toFixed(1)} km/h`}
                                            </span>
                                          </div>
                                          <span className="text-[10px] font-mono text-white/75">
                                            {t('aggregationdiff.topSpeedPeak', {
                                              a: ts.maxSpeedAKmh.toFixed(1),
                                              b: ts.maxSpeedBKmh.toFixed(1),
                                            })}
                                          </span>
                                        </div>
                                      );
                                    }

                                    if (reason.segmentDiff) {
                                      const sd = reason.segmentDiff;
                                      const isBraking =
                                        reason.category === 'braking';
                                      const isAccel =
                                        reason.category === 'acceleration';
                                      const badgeClass = isBraking
                                        ? 'bg-red-500/20 text-red-200 border-red-400/40'
                                        : isAccel
                                        ? 'bg-emerald-500/20 text-emerald-200 border-emerald-400/40'
                                        : 'bg-sky-500/20 text-sky-200 border-sky-400/40';
                                      const iconClass = isBraking
                                        ? 'text-red-300'
                                        : isAccel
                                        ? 'text-emerald-300'
                                        : 'text-sky-300';
                                      const CategoryIcon = isBraking
                                        ? Disc
                                        : isAccel
                                        ? Zap
                                        : ArrowUpRight;

                                      const dA = sd.segmentA
                                        ? getSegDistances(
                                            sd.segmentA,
                                            effectiveTotalLengthM
                                          )
                                        : null;
                                      const dB = sd.segmentB
                                        ? getSegDistances(
                                            sd.segmentB,
                                            effectiveTotalLengthM
                                          )
                                        : null;
                                      const isMatched =
                                        sd.matchStatus !== 'only_a' &&
                                        sd.matchStatus !== 'only_b';
                                      const spreadDiff = reason.spreadDiffM || 0;

                                      return (
                                        <div
                                          key={`reason-${reason.category}-${rIdx}`}
                                          className={`rounded-md border px-2.5 py-1.5 flex flex-col gap-1 ${reasonBoxClass}`}
                                        >
                                          <div className="flex items-center justify-between gap-2 flex-wrap">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                              <span
                                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${badgeClass}`}
                                              >
                                                <CategoryIcon
                                                  size={10}
                                                  className={iconClass}
                                                />
                                                {isBraking
                                                  ? t('aggregationdiff.filterBraking')
                                                  : isAccel
                                                  ? t(
                                                      'aggregationdiff.filterAcceleration'
                                                    )
                                                  : t('aggregationdiff.filterUpshift')}
                                              </span>
                                              <span className="text-[11px] font-semibold text-white">
                                                {formatSignedDistanceHeadline(sd)}
                                              </span>
                                              {isMatched &&
                                                Math.abs(spreadDiff) >= 0.5 && (
                                                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-950/60 border border-white/15 text-white/85">
                                                    {spreadDiff > 0
                                                      ? t(
                                                          'aggregationdiff.widerBy',
                                                          {
                                                            dist: spreadDiff.toFixed(
                                                              1
                                                            ),
                                                          }
                                                        )
                                                      : t(
                                                          'aggregationdiff.tighterBy',
                                                          {
                                                            dist: Math.abs(
                                                              spreadDiff
                                                            ).toFixed(1),
                                                          }
                                                        )}
                                                  </span>
                                                )}
                                            </div>
                                            <span className="text-[10px] font-mono text-white/65">
                                              @{Math.round(reason.distanceM)}m
                                            </span>
                                          </div>
                                          <div className="flex items-center justify-between text-[10px] font-mono">
                                            <span className="text-red-200">
                                              {groupALabel}:{' '}
                                              {dA
                                                ? (sd.segmentA?.sampleCount ?? 0) > 1
                                                  ? `${dA.medDistM.toFixed(1)}m (IQR ${dA.iqrM.toFixed(1)}m)`
                                                  : `${dA.medDistM.toFixed(1)}m`
                                                : '—'}
                                            </span>
                                            <span className="text-blue-200">
                                              {groupBLabel}:{' '}
                                              {dB
                                                ? (sd.segmentB?.sampleCount ?? 0) > 1
                                                  ? `${dB.medDistM.toFixed(1)}m (IQR ${dB.iqrM.toFixed(1)}m)`
                                                  : `${dB.medDistM.toFixed(1)}m`
                                                : '—'}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    }

                                    return null;
                                  })}
                                </div>
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {sessionRemovedAnalysis && (
        <SessionRemovedAnalysisDialog
          open={!!sessionRemovedAnalysis}
          onClose={() => {
            setSessionRemovedAnalysis(null);
            navigate('#/analysis/history');
          }}
          analysis={sessionRemovedAnalysis}
          onDeleted={() => {
            setSessionRemovedAnalysis(null);
            navigate('#/analysis/history');
          }}
        />
      )}
    </div>
  );
}
